/**
 * components/TransactionFormModal.js
 * Create/edit a transaction — PENJUALAN (T1b), PEMBELIAN + TRANSFER (T1c), per PRD §5.4's
 * three variants. One component, `isEdit = mode === "edit"` — mirrors ItemFormModal's
 * mode-split convention. `type` (PENJUALAN|PEMBELIAN|TRANSFER) is required for create mode;
 * for edit mode the caller passes `initial.type` (a transaction's type never changes). A
 * "duplicate" is just a create whose `initial` is a date/time-reset clone of an existing
 * transaction (built by the caller, Penjualan.js/Pembelian.js).
 *
 * TRANSFER notes: the Lokasi toggle is relabeled "Pusat → Cabang"/"Cabang → Pusat" and
 * doubles as the direction selector — `location` stays the single source of truth (the
 * SOURCE bucket, per the data model) and `transferDirection` is derived from it on submit.
 * The counterparty is NOT picked here — it's the reserved branch contact, resolved
 * server-side (App.js's createTransaction/editTransaction) from the direction, so this form
 * only shows it read-only for context and sends no contactId/buyerBlock for TRANSFER at all.
 *
 * No click-outside-closes (matches ItemFormModal/PenyesuaianModal, not the lighter
 * picker/confirm modals) — this is a substantial data-entry form, not a quick action.
 *
 * Phase T1e redesign: each line is a labeled `.txn-item-card` (item picker on its own row,
 * then Quantity/Harga Per Item/Subtotal side by side); Harga/Subtotal/Total show live
 * Indonesian-formatted currency (display-only — `utils/currency.js` parses back to the same
 * raw integer this form always submitted); the Sales field is a searchable `SalespersonPicker`
 * (mirrors `ContactPicker`) with an inline "+ Tambah sales baru…" quick-add instead of a plain
 * `<select>` + a "Kelola Sales" button (that button now lives on the Penjualan page header,
 * opening the same `KelolaSalesModal`); and submitting a transaction with any line that both
 * ends up negative AND makes the item's real current stock WORSE is intercepted by
 * `StockWarningModal` — a non-blocking speed bump, never a wall. (Bug fix: this used to fire on
 * "the result is merely negative," which wrongly warned on a PEMBELIAN line that *improves* an
 * already-negative balance — see `liveStock`/the `flagged` build in `submit()` below.)
 *
 * Phase T2b: the per-line "No. Colly" input was REMOVED from this form — it moved to a
 * dedicated Pengepakan dialog, along with the transaction-level `packing` fields. `collyNo`
 * stays on the line data shape (always `null` at creation now; filled in afterward). **Phase
 * T2c-2**: that standalone dialog (`PengepakanModal.js`) was retired — colly/packing capture
 * now happens inside `components/print/SuratJalanDialog.js`/`ChecklistDialog.js`, each opened
 * from its own print row action, never from this form. The dead "No. Surat Jalan Cabang"
 * field (never read by any write path since T2a) was also removed. The Sales field was
 * anchor-conditional at the time — hidden and unrequired for a PUSAT-anchored transaction,
 * shown and required for a CABANG-anchored one (`needSales = !isPembelian && location ===
 * "CABANG"`) — **since revised: invariant 12 now requires a salesperson on BOTH anchors, so
 * `needSales = !isPembelian` alone; only the branch-first PICKER UI (the "Cabang" filter
 * dropdown ahead of Sales) stays CABANG-only (`needSalesBranchFilter`) — see this file's own
 * inline comments at that code for the current shape. The picker's own OPTIONS are location-
 * filtered on BOTH anchors, though (a later bug fix — a Pusat anchor only ever offers a role
 * whose own `branchId` is `null`, never a branch role): see `filteredSalespeople`'s own comment.**
 * The bare Lunas/Belum
 * Lunas toggle gained a real multi-method payment-rows editor beneath it (method + amount +,
 * for Lainnya, a required note), validated live via `utils/payments.js`'s `validatePayments` —
 * Lunas starts with one row pinned to the live total (auto-resyncing as lines change, only
 * while exactly one row exists); Belum Lunas starts empty. `draft.payments` is a new field this
 * form now sends; `App.js`'s `createTransaction` is what actually persists it.
 *
 * Phase T2b-fix-3: a duplicate-item WARNING (never a block — real invoices legitimately
 * repeat an item across lines, e.g. to carry two different colly numbers) shows on every
 * line whose `internalCode` also appears on another line, matched by code (never by name —
 * two different items can share one). Purely informational; `canSubmit` is untouched.
 *
 * Multi-branch cabang feature: for the ordinary CABANG-anchored PENJUALAN/Cabang→Pusat-transfer
 * case, `branchId` is derived SERVER-SIDE (App.js's `createTransaction`) from the picked
 * salesperson's own branch — `needSales`/`SalespersonPicker`'s underlying derivation is
 * untouched. `needBranchPicker` (renamed/widened from `needDestBranch` in the post-launch fix
 * below) gates a plain, explicitly-picked required branch `<select>` — disabled on edit, since
 * `branchId` is immutable after creation (invariant 19) — for the two cases with no
 * salesperson to derive one from: a Pusat→Cabang transfer (`isTransfer && location ===
 * "PUSAT"`, labeled "Cabang Tujuan"), and (post-launch fix, below) a Cabang-anchored PEMBELIAN
 * (`isPembelian && location === "CABANG"`, labeled just "Cabang" — PEMBELIAN never has a
 * salesperson at all, so there's no filtering flow to build, just this plain required pick).
 *
 * **Post-launch fixes** (a Chalton-confirmed review pass after the multi-branch feature
 * shipped, not new design): (1) the CABANG-anchored Sales field now has its own client-side-
 * only "Cabang" filter ahead of it (`salesBranchFilter` state) — the Sales picker stays empty
 * until a branch is chosen, then shows only `salespeople.filter(s => s.branchId ===
 * salesBranchFilter)`; changing the branch clears an now-mismatched salesperson selection. This
 * is purely a UI narrowing aid — the transaction's real `branchId` still comes exclusively from
 * the server-side derivation off the chosen salesperson's own `branchId` (App.js), never from
 * `salesBranchFilter` directly, so there's no second source of truth. (2) `needBranchPicker`
 * itself was widened to also cover a Cabang-anchored PEMBELIAN, per the paragraph above.
 *
 * **Person/Role restructure, Stage 3 batch 3a**: the inline "+ Tambah sales baru…" quick-add no
 * longer creates a role by itself — `SalespersonPicker`'s `create()` now calls
 * `onRequestCreate(name)`, which this form turns into `roleFormRequest` state
 * (`{ initialQuery }` or `null`) and renders as a nested `SalespersonFormModal` on top of this
 * one (same "a modal can open another modal" precedent `SiblingEditor`'s own "+ Tambah saudara"
 * → `ItemPickerModal` already established). That modal's own `onSubmit` is wired to
 * `onCreateSalespersonRole` (App.js's real create handler) wrapped so a successful create ALSO
 * selects the new role here (`setSalespersonId`) before the nested modal closes itself; a
 * cancelled nested modal just closes with `roleFormRequest` reset to `null` — this form's own
 * state (lines, payments, everything else already typed) is never touched by any of it, since
 * the nested modal is a fully separate component tree. `initialBranchId` is passed through as
 * `salesBranchFilter || undefined` (the SAME branch-context carryover the old inline quick-add
 * used to pass straight to `onCreateSalesperson`), so a role created while a branch is already
 * selected still lands in that same branch by default.
 *
 * **Payment-correction rework**: the Status Pembayaran toggle, Jatuh Tempo field, and
 * Pembayaran card — previously CREATE-mode only, with a static hint in edit mode claiming
 * payment status "tidak diubah dari sini" — are now shown in BOTH modes. This is the ONE
 * place full payment correction lives: an existing row's method/amount can be corrected, a
 * row can be removed entirely (✕), and new rows can be added, exactly like at creation. Edit
 * mode seeds `isLunas`/`payments`/`dueDate` from `initial` once at mount (`isLunas` from
 * `txnStatus(initial)`, `payments` cloned WITH each row's real `id`/`at` intact so
 * `App.js`'s `buildPaymentDiff` can match them and produce a clean correction rather than a
 * remove+add pair, `dueDate` from `initial.dueDate` if present). The toggle itself
 * (`toggleLunas`) is a PURE UI intent signal in edit mode — it never mutates `payments` (only
 * create mode still clears rows on toggle, since those rows have no server-side existence
 * yet); Simpan simply stays blocked (via the now-mode-unconditional `paymentsCheck`, reusing
 * the exact same `validatePayments` messages create mode always showed) until the real rows
 * agree with whatever the toggle currently shows. The Lunas-row auto-sync mechanism
 * (`prevSyncKey`, above) DELIBERATELY stays create-mode only — see its own comment for why
 * extending it to edit mode would violate the "a payment only changes through a deliberate
 * action" principle this whole rework is built on. `buildDraft` now sends `draft.payments` in
 * both modes (a row's `id` present = correction/unchanged, absent = new — `App.js`'s
 * `editTransaction` stamps new ones server-side, mirroring `createTransaction`'s own
 * stamping); `draft.isLunas`/`draft.dueDate` were already sent unconditionally, they simply
 * start being read by `editTransaction` instead of ignored. `recordPayment`/`correctPayment`
 * (the narrow Bayar Tagihan write paths from the prior session) — `correctPayment` is
 * retired entirely now that this form is the one correction surface; `recordPayment` still
 * backs Bayar Tagihan's own quick ADD-only flow, unrelated to this form.
 *
 * Payment-correction rework fix: `hasDuplicateNewMethod` (edit-mode only) blocks Simpan when
 * two rows with NO existing `id` (both added via "+ Tambah metode" in this same edit) share
 * one method — both would be stamped with the identical `at` timestamp server-side, otherwise
 * indistinguishable even with the timestamp now shown on the read-only payment lists
 * (TransactionDetailModal.js/BayarTagihanModal.js). A new row matching an EXISTING row's own
 * method stays allowed — that row already has its own genuine, distinct historical `at`.
 * Client-side only, deliberately — this is a display-disambiguation concern, not a
 * money-correctness invariant, so no matching server-side guard was added to `editTransaction`.
 *
 * paidAt duplicate-prevention feature: a SEPARATE, broader check than `hasDuplicateNewMethod`
 * above — blocks a row (existing OR new, either mode) whose method+paidAt exactly match ANOTHER
 * row anywhere in `payments` (not just other new rows), computed live via `utils/payments.js`'s
 * `hasDuplicatePaymentAgainst` against the whole array every render — so correcting an EXISTING
 * row's date/time into a collision is caught the same way adding a colliding new row is. Amount
 * never factors in; two rows differing in date OR time are allowed (a legitimate backdated
 * payment). Renders as a per-row inline `form-hint` under that row's own Tanggal/Waktu Bayar
 * inputs and folds into `canSubmit` — same inline-blocks-before-save shape every other row-level
 * check in this card already uses, not a submit-time-only validation.
 *
 * Cost items (Brief 1 of the cost-items rollout): a new `CostItemsEditor` card renders directly
 * below the Barang card, above Total Nilai, in both create and edit mode, for all three
 * transaction types (Penjualan/Pembelian/Transfer share the same insertion point — only the
 * add-button labels/type values differ by `txnType`, handled inside that component). `costItems`
 * state/handlers mirror `payments`' own shape exactly (lazy edit-mode seed with real `id`s
 * intact, `costItemsCheck` folded into `canSubmit`, `draft.costItems` sent id-present-means-
 * correction/id-absent-means-new). See utils/costItems.js and the root CLAUDE.md.
 *
 * Cost items Brief 2: this form's own local `total` (below) now folds in
 * `sumCostItemsRaisingTotal(costItems)` — tagihan/tagihanSupplier raise it, biaya never does —
 * so the Lunas-row live-pin sync, the pre-submit `paymentsCheck`, and the displayed "Total
 * Nilai" alert all stay correct as cost items are added/edited, live, exactly like they already
 * do for line qty/harga changes. No new state, no new effect — every one of those already reads
 * this one `total` variable.
 *
 * Corrective pass, Piece 2: `total` also subtracts `sumPotonganCostItems(costItems)` — a
 * "potongan" (discount) row lowers it live the same way a tagihan row raises it, inherited by
 * the same three live behaviors above with no further change.
 *
 * Two-bucket payment split (Barang / Tagihan & Potongan) — shipped across three briefs
 * (create mode + Bayar Tagihan; Edit Transaksi's own full UI; display polish), THEN
 * substantially simplified by a post-launch UX redesign after Chalton's own hands-on QA of the
 * shipped Edit Transaksi UI. **This paragraph describes the CURRENT (redesigned) shape only —
 * see utils/paymentSplit.js and the root CLAUDE.md for the full design/history.**
 *
 * `showBucketUI` activates whenever a live bucket exists OR any payment row — new or existing
 * — already carries real split data (`"splitBarang" in p || "splitTagihanPotongan" in p`, an
 * EXISTENCE check, not a `typeof` one, and independent of whether the row has been saved yet)
 * — checked against LIVE state, no mount-time snapshot needed. This is still
 * PERMANENT once real split data exists, even after the bucket that justified it later
 * vanishes — hiding the section again would let a row's plain Jumlah amount silently desync
 * from a stale, now-invisible split. Once `showBucketUI` is true, EVERY row (new or existing)
 * shows its "Rincian Pembagian" split fields directly — no opt-in link, no "Dianggap 100%..."
 * note, no "Batalkan pembagian." A row that predates the bucket displays PRE-FILLED with
 * `paymentSplit(p)`'s own existing fallback (100% Barang) — computed purely for display, never
 * written to state — so an untouched pre-filled row saves with no split fields at all (still
 * frozen, no new `editLog` diff line); a genuinely new row (`!p.id`) still starts blank, per
 * the original "never auto-fill" rule. The setters (`setPaymentSplitBarang`/
 * `setPaymentSplitTagihanPotongan`) auto-seed the OTHER field from its own current fallback the
 * FIRST time an existing row is touched, so editing just one of the two pre-filled fields
 * converts both into real stored numbers together (otherwise `validateSplitSum` would see one
 * real number and one `undefined`).
 *
 * The two per-bucket toggles still seed from the REAL persisted `initial` transaction's own
 * paid-vs-owed state in edit mode, and still never mutate `payments` on toggle (same "pure UI
 * intent signal" rule the single toggle's own edit-mode `toggleLunas` follows).
 *
 * **Validation surfacing is now ENTIRELY inline — no modal, no two-pass gate.** Per bucket,
 * `utils/paymentSplit.js`'s new `bucketMessage(label, paid, owed, lunasIntent)` decides the ONE
 * message to show (or `null`) purely from paid-vs-owed-vs-toggle — see that function's own
 * header comment for the 4-case priority logic. `canSubmit` simply disables whenever EITHER
 * bucket has a message showing (`!barangMessage && !tagihanMessage`) — no distinction between
 * "stale/saved" and "fresh/typed" data. `BucketOverAllocationModal`, the two-pass
 * `fullOverAllocCheck`/`savedOnlyOverAllocCheck`/`newBucketViolations` gate, and the
 * `barangStale`/`tagihanStale` Check-2 exemption are ALL removed — this was purely a
 * presentation-layer simplification. **`App.js`'s own `checkBucketOverAllocation`/
 * `validateBucketCompleteness` calls in `createTransaction`/`editTransaction`/`recordPayment`
 * are completely untouched** — that server-side guard chain is the real save-time safety net
 * and was never part of this redesign; `bucketMessage` is a new, additive, UI-only function
 * that never replaces or reimplements it.
 */
import React, { useMemo, useState } from "react";
import { fmtQty, fmtDateShort, today, nowTime, combineDateTime, splitDateTime } from "../utils/idGenerators";
import { formatWac } from "../utils/wac";
import { formatCurrencyInput, parseCurrencyInput } from "../utils/currency";
import { applyTxnStockDelta } from "../utils/txnStock";
import { txnStatus, validateTxnTotal } from "../utils/txnStatus";
import { useModalEsc } from "../utils/modalStack";
import { PAYMENT_METHODS, METHOD_LABEL, validatePayments, hasDuplicatePaymentAgainst } from "../utils/payments";
import { validateCostItems, sumCostItemsRaisingTotal, sumPotonganCostItems } from "../utils/costItems";
import {
  paymentSplit, barangOwed, barangPaid, tagihanPotonganOwed, tagihanPotonganPaid,
  validateSplitSum, bucketMessage, bucketOverAllocationMessage,
} from "../utils/paymentSplit";
import ContactPicker from "./ContactPicker";
import SalespersonPicker from "./SalespersonPicker";
import SalespersonFormModal from "./SalespersonFormModal";
import ItemPickerModal from "./ItemPickerModal";
import StockWarningModal from "./StockWarningModal";
import { siblingsOf } from "../utils/siblings";
import SalesRemintWarningModal from "./SalesRemintWarningModal";
import CostItemsEditor from "./CostItemsEditor";
import BiayaLainEditor from "./BiayaLainEditor";
import { useExpenses } from "./ExpensesContext";
import { expensesForTxn, checkExpenseRows } from "../utils/expenses";
import { StatusPill } from "./TxnBadges";
import { resolveContactSalespersonRoles, resolveSalespersonName } from "../utils/salespeople";
import { resolveBranchName } from "../utils/branches";

const TYPE_LABEL = { PENJUALAN: "Penjualan", PEMBELIAN: "Pembelian", TRANSFER: "Transfer" };
// Change 1 (Sales-clarity pass): the two reasons the Sales field can be shown disabled instead
// of its normal picker — see this file's own `needSalesBranchFilter`/`salespersonRoleMissing`
// for when each applies. Two different states, two different sentences: showing the "pick a
// branch" text on an edit whose transaction already HAS a branch (just an unresolvable stored
// salesperson) would be false.
const SALES_PICK_BRANCH_FIRST = "Pilih cabang dulu untuk melihat sales.";
const SALES_ROLE_UNRESOLVABLE = "Sales pada transaksi ini sudah tidak ditemukan — pilih ulang cabang untuk memilih sales yang baru.";
const RESERVED_CONTACT_NAME = { PUSAT_TO_CABANG: "Cabang (internal)", CABANG_TO_PUSAT: "Pusat (internal)" };

const addDays = (dateStr, days) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");
};

// Inverse of addDays — same local-midnight construction, so it's an exact round-trip
// (Indonesia has no DST, so calendar-day math never lands on a fractional day). Used to seed
// the Tempo Pembayaran (days) field from a real stored dueDate on edit.
const diffDays = (fromDateStr, toDateStr) => {
  const a = new Date(`${fromDateStr}T00:00:00`);
  const b = new Date(`${toDateStr}T00:00:00`);
  return Math.round((b - a) / 86400000);
};

export default function TransactionFormModal({
  mode, type, initial,
  items, contacts, salespeople, persons = [], branches = [], avgCosts, siblingLinks = [],
  defaultDueDays = 14,
  onCreateContact, onCreateContactFull, onCreateSalespersonRole,
  onSubmit, onClose,
}) {
  const isEdit = mode === "edit";
  const txnType = initial?.type || type || "PENJUALAN";
  const isPembelian = txnType === "PEMBELIAN";
  const isTransfer = txnType === "TRANSFER";
  const byCode = useMemo(() => new Map(items.map((it) => [it.internalCode, it])), [items]);

  const [date, setDate] = useState(initial?.date || today());
  const [time, setTime] = useState(initial?.time || nowTime());
  const [location, setLocation] = useState(initial?.location || "PUSAT");
  const [contactId, setContactId] = useState(initial?.contactId || null);
  // Internal contacts are app-maintained TRANSFER stand-ins, not real shops — never offer one
  // as a pickable Pembeli/Penjual (Contact model redesign leak fix). A deactivated contact is
  // excluded the same way (Contact model redesign, stage 2) — EXCEPT the currently-selected one
  // (edit mode preloads `contactId` from the persisted transaction), which stays visible and
  // pickable even after being deactivated, mirroring `visibleSalespeople`'s own "an
  // already-picked one keeps working" rule for a deactivated salesperson role. Without this, a
  // deactivated counterparty's name would silently blank out when editing an old transaction
  // that already references it.
  // List-scoping (KontakPage batch 2b): a Pusat-anchored transaction only ever offers a
  // PUSAT-list contact, a Cabang-anchored one only a CABANG-list contact — `location` IS the
  // anchor (for TRANSFER this picker isn't even rendered; the counterparty is server-resolved).
  // The "own already-selected contact" exception below applies for ANY reason it fell out of
  // `base` — a deactivated flag, a list mismatch, or both — so an edit form's real counterparty
  // never silently blanks out here either.
  const pickableContacts = useMemo(() => {
    const base = contacts.filter((c) => !c.internal && !c.deactivated && c.list === location);
    if (contactId && !base.some((c) => c.id === contactId)) {
      const existing = contacts.find((c) => c.id === contactId);
      if (existing) return [...base, existing];
    }
    return base;
  }, [contacts, contactId, location]);
  // No pre-fill, ever, on create — a Cabang transaction always starts with an empty
  // salesperson, requiring an active pick. This used to have TWO fallback tiers beyond
  // `initial?.salespersonId`: `defaultSalespersonId` ("same salesperson as last time") and,
  // before that, `(salespeople[0]?.id ?? "")` ("whoever happens to be first in the array").
  // Both are gone now, not just the arbitrary one. The hidden-salesperson investigation found
  // that ANY pre-selected value — even a genuinely well-intentioned "remembers last used"
  // convenience — is indistinguishable on screen from a value the user actually chose. Someone
  // moving quickly sees a name already sitting in the field, assumes it's correct, and saves;
  // the wrong salesperson's code and branch then end up on a real printed customer invoice with
  // no warning. The one legitimate seed left is `initial?.salespersonId` (edit mode, loading a
  // transaction's OWN already-committed salesperson back — not a default, the actual value).
  const [salespersonId, setSalespersonId] = useState(initial?.salespersonId || "");
  // Multi-branch cabang feature: a required, plain branch pick for the two cases with no
  // salesperson to derive a branch from — a Pusat→Cabang transfer, and (post-launch fix) a
  // Cabang-anchored PEMBELIAN (PEMBELIAN never has a salesperson at all). See `needBranchPicker`
  // below and App.js's `createTransaction`.
  const [branchId, setBranchId] = useState(initial?.branchId || "");
  // Post-launch fix (Fix 2): a client-side-only "which branch" filter for the Sales picker on
  // a CABANG-anchored Penjualan/Cabang→Pusat transfer — the actual `branchId` written to the
  // transaction still comes exclusively from the chosen salesperson's own `branchId`, derived
  // server-side (App.js's createTransaction) — this never competes with that derivation, it
  // just narrows which salespeople are offered. Pre-selects the branch matching the CURRENT
  // salesperson on edit so the picker isn't confusingly empty when opening an existing
  // transaction.
  //
  // No pre-fill on create: seeded from the SAME `initial?.salespersonId` expression
  // `salespersonId` itself uses above, so the two states can never start out of step — in
  // create mode `initial` is always null, so this is always "" and the picker always starts
  // genuinely blank ("Pilih cabang dahulu."), on purpose, every time.
  //
  // Bug fix: the primary lookup resolves the branch through the STORED salesperson's own
  // `branchId` (so a role that's since moved branches still shows this transaction's OWN
  // historical branch, not its role's current one) — but that lookup silently came back
  // "" whenever the role can no longer supply a branchId at all: a permanently-deleted role
  // (`.find` returns nothing), or an old-data role that exists but is anchored to the WRONG
  // location for this transaction (`branchId: null` on a CABANG-anchored txn — reachable
  // before invariant checks existed, see `isCurrentRole`'s own comment below). Either way the
  // transaction's real `branchId` was already sitting right there on `initial`, unread — the
  // Cabang `<select>` rendered its blank placeholder and the Sales field disabled itself with
  // no way out short of the user re-picking a branch that was already correct. Falling back to
  // `initial?.branchId` closes both: the field is usable on mount, and the dropdown shows the
  // transaction's real, stored branch instead of contradicting it.
  const [salesBranchFilter, setSalesBranchFilter] = useState(
    () => salespeople.find((s) => s.id === initial?.salespersonId)?.branchId ?? initial?.branchId ?? ""
  );
  // Person/Role restructure, Stage 3 batch 3a: the inline quick-add's nested modal request —
  // `null` when not showing, `{ initialQuery }` while `SalespersonFormModal` is open on top of
  // this form. See this file's own header comment for the full flow.
  const [roleFormRequest, setRoleFormRequest] = useState(null);
  // Contacts/salespeople link-up, Part 2: contact-driven Sales resolution.
  // `contactRoleNarrowing`: `null` (no narrowing/gap to explain) or the "multiple"/"noRoleHere"
  // result of `resolveContactSalespersonRoles` for the CURRENTLY selected contact — drives both
  // the narrowed picker list and the explanatory hint below the Sales field. Only ever set by
  // `applyContactRoleResolution` (a deliberate contact/location/branch change), never at mount —
  // an edit form's own already-stored salesperson must never be silently touched just by
  // opening the form (see this file's own resolution helper for the full reasoning).
  const [contactRoleNarrowing, setContactRoleNarrowing] = useState(null);
  // The role id the LAST contact-resolution pass itself picked (the "single valid role"
  // case) — lets the JSX show a small "terisi otomatis dari kontak" note only while the
  // current `salespersonId` is still exactly that auto-filled value, never once the user (or a
  // later resolution pass) has moved it somewhere else.
  const [autofilledRoleId, setAutofilledRoleId] = useState(null);
  // Change 3 (edit-mode salesperson-swap note): a plain string (or `null`) describing what a
  // CONTACT change just did to an already-set Sales value — the old name and the new one, or
  // that it was cleared. Set ONLY from `pickContact` (never from `handleLocationChange` or the
  // Cabang branch-select's own onChange, which can also mutate `salespersonId` but don't
  // represent "the contact changed" — those two clear it instead, since a note describing a
  // value the anchor just moved away from would be misleading). Cleared on any manual real
  // selection (`commitSalespersonSelection`) — a deliberate re-pick supersedes whatever the
  // auto-swap note was explaining. Edit mode only, by construction of where it's ever set.
  const [salespersonSwapNote, setSalespersonSwapNote] = useState(null);
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState(initial?.supplierInvoiceNo || "");
  const [buyerName, setBuyerName] = useState(initial?.buyerBlock?.name || "");
  const [buyerAddress, setBuyerAddress] = useState(initial?.buyerBlock?.address || "");
  // Address picker (Contact model redesign, stage 2 follow-up): mirrors `autofilledRoleId`
  // above exactly — an ephemeral, NEVER-persisted id naming which of the picked contact's
  // `addresses[]` entries `buyerAddress` currently equals, so the "Alamat terisi otomatis dari
  // kontak yang dipilih." hint (below) can tell the user where the visible text came from and
  // vanish the instant they type something else. Set together with `buyerAddress` by
  // `pickContact`/`pickAddress` only; cleared by the Alamat Tercetak input's own `onChange` and
  // by `handleLocationChange`'s existing contact-clearing branch. Never at mount — an edit
  // form's own already-frozen `buyerBlock.address` is never matched against the contact's
  // CURRENT addresses just by opening the form (same "don't touch a stored value at mount" rule
  // `contactRoleNarrowing`/`autofilledRoleId` already follow).
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [lines, setLines] = useState(() => (initial?.lines || []).map((l) => ({ ...l })));
  const [isLunas, setIsLunas] = useState(() => (isEdit && initial ? txnStatus(initial) === "LUNAS" : true));
  // Two-bucket payment split. Brief 2: edit mode now seeds each toggle from the REAL
  // persisted `initial` transaction's own paid-vs-owed state per bucket (mirrors the existing
  // `isLunas` seed, `txnStatus(initial) === "LUNAS"`, one level deeper) — create mode keeps
  // Brief 1's own default of both Lunas.
  const [barangLunas, setBarangLunas] = useState(() =>
    (isEdit && initial ? barangPaid(initial) === barangOwed(initial) : true));
  const [tagihanPotonganLunas, setTagihanPotonganLunas] = useState(() =>
    (isEdit && initial ? tagihanPotonganPaid(initial) === tagihanPotonganOwed(initial) : true));
  // Tempo Pembayaran (Hari) — a UI-only day-count; the real stored value is still a date
  // (`dueDate`, derived below). Edit-mode seed basis: days FROM the transaction's own date TO
  // the real stored dueDate, recomputed backward — never a fabricated fresh default when a
  // real dueDate already exists. Create mode / no stored dueDate falls back to the
  // Pengaturan-configured default.
  //
  // Stuck-zero / leading-zero fix: this is a RAW TEXT state (`dueDaysText`), not a number —
  // mirrors PengaturanModal.js's own "Default Jatuh Tempo (Hari)" field, which already got this
  // right. `onChange` never coerces (see the JSX below), so the field can go genuinely empty
  // mid-edit — the old `Number(e.target.value) || 0` immediate-coercion pattern is exactly what
  // made clearing the field instantly snap to "0" (`Number("") === 0`), after which more typed
  // digits appended AFTER that stuck "0" instead of replacing it.
  const [dueDaysText, setDueDaysText] = useState(() =>
    String(initial?.dueDate ? diffDays(initial?.date || today(), initial.dueDate) : defaultDueDays)
  );
  // The REAL minimum-of-1 enforcement — not just the cosmetic `min="1"` HTML attribute, which
  // does nothing against raw text entry of "0"/""/a negative number. `null` while blank,
  // non-numeric, zero, or negative; `canSubmit` below reads this to genuinely block Simpan,
  // not just style the field differently.
  const dueDaysNum = (() => {
    const n = Number(dueDaysText);
    return Number.isFinite(n) && n >= 1 ? Math.round(n) : null;
  })();
  // Derived, not stored — recalculates live as EITHER the day-count OR the transaction's own
  // date changes, so correcting the transaction date pushes the computed due date along with
  // it (dueDate is genuinely `date + term`, the same relationship Sisa Tagihan has to its own
  // inputs) while the day-count itself stays independently, manually editable at any time.
  // `null` while `dueDaysNum` is (see above) — never a fabricated date for an invalid day-count.
  const dueDate = dueDaysNum != null ? addDays(date, dueDaysNum) : null;
  // paidAt (a stored single ISO string) is split into paidAtDate/paidAtTime for the two
  // date+time inputs below — never held as a combined ISO value while the row is being
  // edited, mirroring how the transaction's own `date`/`time` stay separate the whole time.
  const [payments, setPayments] = useState(() => (isEdit && initial ? (initial.payments || []).map((p) => {
    const { date: paidAtDate, time: paidAtTime } = splitDateTime(p.paidAt || p.at);
    return { ...p, paidAtDate, paidAtTime };
  }) : []));
  // Cost items (Brief 1) — same lazy-seed pattern as `payments`: edit mode clones every row
  // from `initial.costItems` WITH its real `id` intact (so a future edit's id-matched patch
  // can tell "corrected" from "new"); create mode starts empty.
  const [costItems, setCostItems] = useState(() => (isEdit && initial ? (initial.costItems || []).map((c) => ({ ...c })) : []));
  // Biaya Lain-Lain (2026-10-11): this transaction's own costs, kept in `data.expenses` and
  // edited ONLY here (the one editing surface — see utils/expenses.js). Loaded once at mount,
  // like every other field of this form; sent as `draft.expenses` (id present = correction,
  // absent = new, missing = removed) and synced by App.js's create/editTransaction.
  const { expenses: allExpenses } = useExpenses();
  const [biayaLain, setBiayaLain] = useState(() => (isEdit && initial
    ? expensesForTxn(allExpenses, initial.id).map((e) => ({ id: e.id, category: e.category, paidTo: e.paidTo || "", amount: e.amount, note: e.note || "" }))
    : []));
  const biayaLainCheck = checkExpenseRows(biayaLain);
  const [pickerMode, setPickerMode] = useState(null); // null | "add" | { replaceIndex }
  const [pendingDraft, setPendingDraft] = useState(null); // draft awaiting Peringatan Stok confirm
  const [warningLines, setWarningLines] = useState([]);
  // Bug fix ("warn before abandoning a number"): a draft awaiting the sales-remint confirm, held
  // in the SAME `pendingDraft` slot as the stock warning above (never both at once — see this
  // file's own render-site comment). `null` or `{oldName, newName, oldInvoiceNumber}`.
  const [pendingRemintWarning, setPendingRemintWarning] = useState(null);

  useModalEsc(onClose);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  // Silent-defaults fix (2026-10-12): no method until one is picked — a bank transfer left on a
  // pre-chosen "Tunai" used to be recorded as cash. validatePaymentSet blocks an empty method.
  const addPayment = () => setPayments((ps) => [...ps, {
    method: "", amount: null, methodNote: null, paidAtDate: today(), paidAtTime: nowTime(),
  }]);
  const removePayment = (i) => setPayments((ps) => ps.filter((_, idx) => idx !== i));
  const setPaymentMethod = (i, method) => setPayments((ps) => ps.map((p, idx) =>
    idx === i ? { ...p, method, methodNote: method === "LAINNYA" ? (p.methodNote || "") : null } : p));
  const setPaymentAmount = (i, amount) => setPayments((ps) => ps.map((p, idx) => (idx === i ? { ...p, amount } : p)));
  const setPaymentNote = (i, methodNote) => setPayments((ps) => ps.map((p, idx) => (idx === i ? { ...p, methodNote } : p)));
  const setPaymentPaidAtDate = (i, paidAtDate) => setPayments((ps) => ps.map((p, idx) => (idx === i ? { ...p, paidAtDate } : p)));
  const setPaymentPaidAtTime = (i, paidAtTime) => setPayments((ps) => ps.map((p, idx) => (idx === i ? { ...p, paidAtTime } : p)));
  // Two-bucket payment split — a genuinely NEW row (no `id`) is still never auto-filled (point 3
  // of the original confirmed design): its split fields start undefined and stay that way until
  // the user deliberately types into them. An EXISTING row (`p.id` present) that predates the
  // bucket shows its own split fields PRE-FILLED for display (via `paymentSplit(p)`'s existing
  // fallback, in the JSX below — never written to state on render). The first time the user
  // types into EITHER of its two fields, the OTHER one is auto-seeded from that same fallback
  // right here, so both become real stored numbers together — otherwise `validateSplitSum` and
  // the outgoing patch's `typeof`-gated split inclusion would see one real number and one
  // `undefined` after only one field was touched.
  //
  // 2nd-round fix: the seed guard below checks property EXISTENCE (`"splitBarang" in p`), not
  // `typeof p.split<Other> !== "number"`. Once `parseCurrencyInput` can return `null` for a
  // cleared field (the same round's own currency fix), a `typeof`-based guard can no longer
  // tell "this row has never been touched" apart from "this row was touched, then cleared back
  // to blank" — both read as "not a number." Re-checking `typeof` on every call would silently
  // re-seed (overwrite) a field the user just deliberately cleared, the moment they also clear
  // the OTHER field on the same row. Existence, not typeof, is what "touched" has always meant
  // for a stored row (see `utils/paymentSplit.js`'s own header comment) — once either key
  // exists, every later edit only ever updates its own key, never re-seeding the other.
  const setPaymentSplitBarang = (i, splitBarang) => setPayments((ps) => ps.map((p, idx) => {
    if (idx !== i) return p;
    if (p.id && !("splitBarang" in p) && !("splitTagihanPotongan" in p)) {
      return { ...p, splitBarang, splitTagihanPotongan: paymentSplit(p).tagihanPotongan };
    }
    return { ...p, splitBarang };
  }));
  const setPaymentSplitTagihanPotongan = (i, splitTagihanPotongan) => setPayments((ps) => ps.map((p, idx) => {
    if (idx !== i) return p;
    if (p.id && !("splitBarang" in p) && !("splitTagihanPotongan" in p)) {
      return { ...p, splitTagihanPotongan, splitBarang: paymentSplit(p).barang };
    }
    return { ...p, splitTagihanPotongan };
  }));

  // Payment-correction rework: the toggle is a PURE UI intent signal in edit mode — it must
  // never mutate an already-real payment record on its own. In create mode, payments are
  // always freshly-typed rows with no server-side existence yet, so clearing on toggle is
  // still the right create-mode behavior (unchanged).
  const toggleLunas = (val) => {
    setIsLunas(val);
    if (!isEdit) setPayments([]);
  };

  // Bug fix (manual-testing report): `freshContact`, when provided, is a contact ContactPicker
  // just created THIS SAME TICK via its own full-form flow — this component's `contacts` prop
  // still reflects the pre-create array (App.js's `update()` only lands on the next render), so
  // `contacts.find` below would silently resolve to nothing and skip both the buyer-block fill
  // AND the Sales autofill, even though picking that exact same contact a moment later (once
  // `contacts` has caught up) works correctly. Falling back to `freshContact` makes create-then-
  // resolve behave identically to pick-existing-then-resolve, rather than only working the
  // second time.
  const pickContact = (id, freshContact) => {
    // Change 3: capture the value BEFORE this contact change touches it — `applyContactRoleResolution`
    // below always clears-then-reapplies, so this is the one place that still has "what it was."
    const oldSalespersonId = salespersonId;
    setContactId(id);
    const c = contacts.find((c) => c.id === id) || freshContact || null;
    // Contact model redesign, stage 2 (address picker): a contact has `addresses[]`, not a
    // single `.address` string, and with two or more entries there's no guaranteed default
    // (`utils/contacts.js`'s own `normAddresses` never auto-picks one among several). What CAN
    // be trusted: `normAddresses` forces exactly one `isDefault:true` whenever a contact has
    // exactly ONE address, so "does this contact have a marked default" already covers both the
    // single-address case and the several-addresses-with-a-chosen-default case uniformly — no
    // separate branch needed. Auto-filling from that default is safe specifically BECAUSE it's
    // shown, visibly, in this same input the user can still freely edit — never a value frozen
    // in behind the scenes. A contact with 2+ addresses and no marked default still gets NOTHING
    // auto-filled (`def` is `null`) — the multi-address `<select>` below is what lets the user
    // choose one; nothing here ever guesses among several.
    if (c) {
      setBuyerName(c.name);
      const def = (c.addresses || []).find((a) => a.isDefault) || null;
      setBuyerAddress(def ? def.text : "");
      setSelectedAddressId(def ? def.id : null);
    }
    // Contacts/salespeople link-up, Part 2: a contact pick always re-resolves the Sales field
    // from scratch — see `applyContactRoleResolution`'s own header comment (defined below,
    // referenced here via closure — this function isn't invoked until well after that later
    // `const` has been assigned, on the very same render pass).
    const resolution = applyContactRoleResolution(c || null, currentSalesBranchIdOrPending());
    // Change 3: build the inline "what changed" note from the resolution's own outcome — never
    // from a later read of `salespersonId` state (that setter hasn't committed yet this tick).
    // Deliberately narrow to THIS call site — `handleLocationChange` and the Cabang branch-
    // select's own onChange call `applyContactRoleResolution` too, but neither represents "the
    // contact changed," so neither builds this note (they clear it instead, see their own calls
    // to `setSalespersonSwapNote(null)`).
    if (isEdit && oldSalespersonId && resolution) {
      const newSalespersonId = resolution.kind === "single" ? resolution.role.id : "";
      if (newSalespersonId !== oldSalespersonId) {
        const oldSp = salespeople.find((s) => s.id === oldSalespersonId);
        const oldName = oldSp ? resolveSalespersonName(persons, oldSp) : "sales sebelumnya";
        if (newSalespersonId) {
          const newSp = salespeople.find((s) => s.id === newSalespersonId);
          const newName = newSp ? resolveSalespersonName(persons, newSp) : "sales baru";
          setSalespersonSwapNote(`Sales berubah dari ${oldName} menjadi ${newName} karena kontak diganti.`);
        } else {
          const reason = resolution.kind === "noPeople"
            ? " — kontak baru belum tertaut ke sales manapun."
            : "";
          setSalespersonSwapNote(`Sales sebelumnya (${oldName}) dikosongkan karena kontak diganti${reason}`);
        }
      } else {
        setSalespersonSwapNote(null);
      }
    } else {
      setSalespersonSwapNote(null);
    }
  };

  // The multi-address `<select>`'s own onChange (only rendered when the picked contact has 2+
  // addresses — see the JSX below). A deliberate pick always overwrites `buyerAddress`, visibly,
  // same as any other explicit action in this form — mirrors `pickContact` filling it from a
  // default, just triggered by hand instead of automatically.
  const pickAddress = (addressId) => {
    const c = contactId ? contacts.find((x) => x.id === contactId) : null;
    const addr = (c?.addresses || []).find((a) => a.id === addressId) || null;
    if (addr) {
      setBuyerAddress(addr.text);
      setSelectedAddressId(addr.id);
    }
  };

  const openAddPicker = () => setPickerMode("add");
  const openReplacePicker = (i) => setPickerMode({ replaceIndex: i });
  const closePicker = () => setPickerMode(null);
  const confirmPicker = (codes) => {
    if (pickerMode === "add") {
      const newLines = codes.map((code) => {
        const it = byCode.get(code);
        return { internalCode: code, name: it?.name || code, qty: 1, unitPrice: null, collyNo: null };
      });
      setLines((ls) => [...ls, ...newLines]);
    } else if (pickerMode && typeof pickerMode === "object") {
      const code = codes[0];
      const it = byCode.get(code);
      // A manual re-pick is no longer a substitution — drop the form-only substituteFor hint.
      setLines((ls) => ls.map((l, i) => {
        if (i !== pickerMode.replaceIndex) return l;
        const { substituteFor, ...rest } = l;
        return { ...rest, internalCode: code, name: it?.name || code };
      }));
    }
    closePicker();
  };

  const goodsTotal = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unitPrice) || 0), 0);
  // Cost items Brief 2: tagihan/tagihanSupplier raise the real total the same way a goods line
  // does; biaya never does (see utils/costItems.js's sumCostItemsRaisingTotal). Corrective pass
  // Piece 2: potongan lowers it, the mirror image — this is the one place that needs a manual
  // update per cost-item type, since this form deliberately keeps its own local `total` rather
  // than importing utils/txnStatus.js's txnTotal (see this file's own header comment).
  const total = goodsTotal + sumCostItemsRaisingTotal(costItems) - sumPotonganCostItems(costItems);
  // Total Nilai must never go negative — see utils/txnStatus.js's own validateTxnTotal for why
  // this is validated as its own explicit rule rather than left to ride on paymentsCheck's own
  // sum<=total invariant, which already blocks it today but for the wrong stated reason.
  const totalCheck = validateTxnTotal(total);

  // Two-bucket payment split. `hasBucket` reuses the form's own existing `total`/`goodsTotal`
  // locals, no new total calculation — this IS `tagihanPotonganOwed(txn) > 0` from
  // utils/paymentSplit.js, just expressed against this form's own already-computed numbers.
  //
  // `showBucketUI` activates in EDIT mode whenever a live bucket exists, OR whenever ANY
  // payment row — new or existing — already carries real split data. This permanence is
  // deliberate, confirmed during plan review: once a row has real split data, hiding its own
  // fields again (because the bucket later vanished) would let its plain Jumlah amount
  // silently drift out of sync with a now-invisible stale split — the section staying visible
  // for the rest of this transaction's life is the accepted cost of closing that hole
  // categorically, not a bug.
  const hasBucket = total - goodsTotal > 0;
  // Permanent once real split data exists on ANY row — new or existing — checked against LIVE
  // state (no mount-time snapshot needed). 2nd-round fix: existence (`"splitBarang" in p`), not
  // `typeof ... === "number"` — a row that's been touched but currently has one or both split
  // fields cleared to `null` must still count as "has real split data," or the section could
  // vanish mid-edit (see the setters' own updated comment above for the full reasoning).
  //
  // showBucketUI-permanence fix (a later, separate round): this check used to also require
  // `p.id`, restricting permanence to rows that already existed BEFORE the current edit
  // session. A brand-new row (`!p.id`, added via "+ Tambah metode" in the SAME session) could
  // be given a real, valid split while a bucket was live, then have the section vanish out from
  // under it the instant the cost item that justified the bucket was removed later in that same
  // session — `barangPaidLocal`/`tagihanPaidLocal` (below) kept summing that row's real values
  // regardless (`rowSplit` reads a new row's raw fields directly, independent of
  // `showBucketUI`), so a genuine over-allocation could still exist, correctly blocking
  // `canSubmit` (which has no `showBucketUI` qualifier on either over-alloc message) — but with
  // `showBucketUI` wrongly false, neither the message (gated `showBucketUI && !bothBucketsLive`
  // in the JSX below) nor the "Rincian Pembagian" box itself (gated `showBucketUI` alone) could
  // render, leaving Simpan silently disabled with no visible field to fix it through except
  // deleting the whole row and re-adding a plain one. `id` was never actually load-bearing to
  // "has real split data" — it only happened to correlate with it, since the split-entry boxes
  // (the ONLY write site for these two fields) are themselves gated on `showBucketUI` alone: a
  // row can never acquire real split data before `showBucketUI` was already true at the moment
  // of typing (see `addPayment`, which seeds neither key at all), so dropping the `p.id`
  // requirement can never make a genuinely blank/never-touched row wrongly "permanent."
  const showBucketUI = hasBucket || payments.some(
    (p) => "splitBarang" in p || "splitTagihanPotongan" in p
  );
  const barangOwedLocal = goodsTotal;
  const tagihanOwedLocal = total - goodsTotal;
  // A row's EFFECTIVE split for display/validation/live totals: an existing row (`p.id`)
  // resolves through paymentSplit()'s own real/fallback rule (so a pre-filled, untouched frozen
  // row reads as valid, 100% Barang); a genuinely new row reads its own raw, possibly-undefined
  // fields (so it contributes NOTHING to either bucket, and stays invalid/blank, until the user
  // actually types into it — "unsplit" means "not decided yet" for a new row, never "assumed
  // Barang").
  // 2nd-round fix: for an existing row, use the raw fields directly whenever EITHER key exists
  // (the row has been touched, even if a field is currently cleared to `null` mid-edit) —
  // falling back to `paymentSplit(p)`'s legacy display only for a genuinely untouched row.
  // Previously AND-typeof-gated, which meant clearing just ONE field of an already-split row
  // would flip this to the fallback, silently replacing the SIBLING field's own displayed value.
  //
  // Moved above barangPaidLocal/tagihanPaidLocal (was defined further down, only for the
  // split-field display values and allSplitsValid) so those two totals can reuse it too — see
  // their own updated comment just below for why.
  const rowSplit = (p) => {
    if (!p.id) return { barang: p.splitBarang, tagihanPotongan: p.splitTagihanPotongan };
    return ("splitBarang" in p || "splitTagihanPotongan" in p)
      ? { barang: p.splitBarang, tagihanPotongan: p.splitTagihanPotongan }
      : paymentSplit(p);
  };
  // Reuses rowSplit(p) — NOT paymentSplit(p) directly (premature/misleading bucket-message fix).
  // paymentSplit(p) alone is the real, persisted-data fallback rule ("unsplit means frozen, 100%
  // Barang") — correct for an OLD row that predates a bucket, wrong for a brand-new row the user
  // is still actively typing into: summing paymentSplit(p) here used to silently count a fresh,
  // not-yet-split row's WHOLE amount as Barang the instant Jumlah was typed, showing red
  // validation text against a split the user hadn't decided yet. rowSplit(p) already draws
  // exactly this distinction (used elsewhere in this file for the split-field display values and
  // allSplitsValid) — reused here rather than a second, parallel rule. `Number(x) || 0` coerces
  // rowSplit's possibly-undefined/null fields for arithmetic, the same convention this file uses
  // everywhere else. An OLD/frozen row is unaffected (rowSplit falls back to paymentSplit(p) for
  // it, identical to before); a fully-split row (old or new) is unaffected too (rowSplit returns
  // the same real numbers paymentSplit would).
  const barangPaidLocal = payments.reduce((s, p) => s + (Number(rowSplit(p).barang) || 0), 0);
  const tagihanPaidLocal = payments.reduce((s, p) => s + (Number(rowSplit(p).tagihanPotongan) || 0), 0);
  // The single existing Status Pembayaran intent collapses into the two bucket toggles (or vice
  // versa) — this is the ONE value every existing downstream read of `isLunas` now uses.
  //
  // Single-toggle redesign (replaces the earlier aggregate-Lunas override — see this file's own
  // top-of-file design note and the root CLAUDE.md for the full history of what that was and why
  // it was superseded): two REAL, independently-meaningful per-bucket toggles only make sense
  // when BOTH buckets genuinely owe something. Once EITHER bucket's owed clamps to zero/negative,
  // that bucket's own toggle is structurally inert — `validateBucketCompleteness`/`bucketMessage`
  // already auto-pass it unconditionally regardless of what the toggle says — so showing it next
  // to the OTHER (real) toggle is actively misleading, not just cosmetically redundant. This is
  // symmetric across BOTH buckets, not just Tagihan & Potongan (the common case): a `goodsTotal
  // === 0` line (a free/promotional item) makes BARANG's own toggle exactly as inert, for the
  // identical structural reason — found during this redesign's own review, not assumed away.
  const bothBucketsLive = barangOwedLocal > 0 && tagihanOwedLocal > 0;
  // Whenever `bothBucketsLive` is false, this form falls all the way back to the SAME single
  // plain Lunas/Belum-Lunas toggle (`isLunas`) this app used before the two-bucket feature ever
  // existed — validated the SAME way, against the REAL `total` (never `barangOwedLocal`
  // specifically) via `paymentsCheck` below. This is not a third validation path: it's the
  // existing state-3 (no-bucket) code path, now also reached whenever either bucket alone would
  // make a real per-bucket toggle meaningless. "I paid everything owed" and "click Lunas" are the
  // same action again, with no override needed to make them agree.
  const effectiveIsLunas = bothBucketsLive ? (barangLunas && tagihanPotonganLunas) : isLunas;
  // The one inline message per bucket (or null) — see utils/paymentSplit.js's bucketMessage for
  // the 4-case priority logic. Gated on `bothBucketsLive`, NOT `showBucketUI` — once either
  // bucket's own toggle is inert (see above), showing its "Status Pembayaran X: ..." completeness
  // text would reference a toggle that no longer renders anywhere in this state; the split-entry
  // boxes themselves stay gated by `showBucketUI` alone (a fully independent axis — see the JSX).
  const barangMessage = bothBucketsLive ? bucketMessage("Barang", barangPaidLocal, barangOwedLocal, barangLunas) : null;
  const tagihanMessage = bothBucketsLive
    ? bucketMessage("Tagihan & Potongan", tagihanPaidLocal, tagihanOwedLocal, tagihanPotonganLunas)
    : null;
  // Toggle-agnostic over-allocation protection for whenever `!bothBucketsLive` (a live bucket UI
  // showing via the permanence rule, but with no real per-bucket toggle left to surface
  // `bucketMessage`'s own case-1/2 text through). Without this, a STALE split (e.g. a row that
  // already carried real `splitTagihanPotongan` money from before its own bucket vanished) could
  // violate `checkBucketOverAllocation` server-side with zero client-side warning — and since
  // `Penjualan.js`'s own `submitForm` closes this modal unconditionally regardless of whether the
  // write actually succeeded, that would be a genuine silent-fail regression this redesign must
  // not introduce. Reuses `bucketOverAllocationMessage` (already used server-side by App.js and
  // by BayarTagihanModal.js's own equivalent hint — not a new function) against the numbers this
  // file already computes above (Fix 2's `rowSplit`-corrected `barangPaidLocal`/`tagihanPaidLocal`
  // — no second parallel owed/paid computation, no synthetic txnLike object needed just to get
  // the same two numbers back out of `checkBucketOverAllocation` itself). Computed unconditionally
  // (cheap, and provably redundant-but-harmless whenever `bothBucketsLive` — `bucketMessage`'s own
  // case-1 condition, `paid > effectiveOwed`, is byte-identical to this trigger, so in that state
  // the two can never disagree) but only RENDERED in the JSX below when `!bothBucketsLive` (state
  // 1 already shows the equivalent fact via `barangMessage`/`tagihanMessage`'s own case 1/2 text).
  const barangOverAllocMessage = barangPaidLocal > Math.max(0, barangOwedLocal)
    ? bucketOverAllocationMessage({ label: "Barang", owed: barangOwedLocal, paid: barangPaidLocal })
    : null;
  const tagihanOverAllocMessage = tagihanPaidLocal > Math.max(0, tagihanOwedLocal)
    ? bucketOverAllocationMessage({ label: "Tagihan & Potongan", owed: tagihanOwedLocal, paid: tagihanPaidLocal })
    : null;
  // Every row's split must sum to its own Jumlah once the section is live — a pre-filled,
  // untouched row already satisfies this via rowSplit()'s own fallback, so it never blocks
  // Simpan on its own.
  const allSplitsValid = !showBucketUI || payments.every((p) => {
    const s = rowSplit(p);
    return validateSplitSum(p.amount, s.barang, s.tagihanPotongan);
  });

  // Keep a lone Lunas payment row pinned to the live total as lines change — resyncs only when
  // `total`/`isLunas` change, never on the amount field's own keystrokes, so a manual edit is
  // never clobbered mid-typing. Neither Lunas nor Belum Lunas auto-seeds a row anymore (removed
  // — a payment row only ever exists once the user clicks "+ Tambah metode"), so this stays
  // inert (payments.length is 0) until that happens; once the user has added their one row, this
  // keeps it pinned exactly as it would have for the old auto-seeded row. The moment a second
  // row exists, syncing stops (the user is now managing a manual split, and validatePayments
  // already catches a mismatch on submit).
  //
  // Adjusted DURING render (not via useEffect+setState) on purpose — React's own documented
  // pattern for "derive state from another value that changed." A post-paint effect would
  // commit/paint the stale (pre-sync) render first, which on this fully-unmemoized form was a
  // real, visible stutter plus a one-frame flash of a bogus "Lunas harus berjumlah tepat sama
  // total" validation message on every Qty/Harga keystroke. Comparing against a tracked
  // `prevSyncKey` (not against `payments` itself) reproduces the effect's exact firing
  // condition — only when total/isLunas actually change, never merely because the single row's
  // amount happens to differ from total (which is also true immediately after a manual edit).
  //
  // Payment-correction rework: stays CREATE-mode only (`!isEdit`) — deliberately NOT extended
  // to edit mode. A payment record only ever changes through a deliberate, visible action (the
  // same principle that already rules out blur-commit auto-save for this feature); silently
  // re-pinning an EXISTING, real payment row's amount because an unrelated item price/qty
  // changed would violate that same principle from the opposite direction. In edit mode,
  // changing a line leaves `payments` byte-identical — if that makes Sisa nonzero, status
  // simply flips to Belum Lunas on its own (ordinary derived txnStatus), and the user corrects
  // the payment amount as its own separate, deliberate edit if that's also wrong.
  const syncKey = `${total}|${effectiveIsLunas}`;
  const [prevSyncKey, setPrevSyncKey] = useState(syncKey);
  if (!isEdit && syncKey !== prevSyncKey) {
    setPrevSyncKey(syncKey);
    if (effectiveIsLunas && payments.length === 1) {
      setPayments((ps) => [{ ...ps[0], amount: total }]);
    }
  }

  // Invariant 12 (revised): a salesperson is required on BOTH anchors now — a Pusat sale
  // genuinely has one too, it's just never shown on a printed Pusat document. `needSales` is
  // therefore type-conditional only (PEMBELIAN never has one); anchor no longer decides whether
  // the field shows, only whether it's branch-filtered (below).
  const needSales = !isPembelian;
  // `anchor === location` for both PENJUALAN and TRANSFER in this form (unchanged).
  const needSalesBranchFilter = needSales && location === "CABANG";
  // Person/Role restructure, Stage 3 batch 3b, Part D: a deactivated role can never be a NEW
  // pick, but an edit that already carries one must keep showing/resolving it (App.js's own
  // create-vs-edit asymmetry for `active`, mirrored here so the picker never contradicts what a
  // save would actually do). `visibleSalespeople` is every ACTIVE role, plus — edit mode only —
  // the transaction's own CURRENT salesperson even if it has since been deactivated; a create
  // form has no `initial`, so this reduces to "active roles only" there, unconditionally.
  const visibleSalespeople = salespeople.filter((s) => s.active !== false || (isEdit && s.id === initial?.salespersonId));
  // A role belonging to the transaction's own already-selected salesperson (edit mode only) —
  // the same "keep it resolvable even once filtered out" exception `visibleSalespeople` above
  // already applies to `active`, extended here to LOCATION too (a bug fix, see the root doc's
  // own "roles pinned to their location" entry for the wider Stage 5 context this bug predates
  // and the write-path guard now enforcing it): a transaction whose own role doesn't match its
  // anchor/branch — reachable in old data, since nothing used to check this on write — must stay
  // editable (e.g. to fix an unrelated field) without the Sales field silently going blank.
  const isCurrentRole = (s) => isEdit && s.id === initial?.salespersonId;
  // The Sales picker's own LOCATION filter (the bug this fix closes): a Pusat anchor has no
  // branch, but it is NOT "every role anywhere" — it means ONLY a role actually AT Pusat
  // (`branchId === null`). A Cabang anchor already filtered to the chosen branch's own roles;
  // that half is unchanged. Without this, a person holding several roles (Pusat + several
  // Cabang branches) showed up once per role, identically, with no way to tell them apart on a
  // Pusat sale, where only the Pusat role is actually relevant.
  const filteredSalespeople = !needSales ? []
    : !needSalesBranchFilter ? visibleSalespeople.filter((s) => s.branchId === null || isCurrentRole(s))
    : salesBranchFilter ? visibleSalespeople.filter((s) => s.branchId === salesBranchFilter || isCurrentRole(s)) : [];
  // Bug fix (manual-testing report, "the picker hands back a role from another branch,
  // unlabelled"): `isCurrentRole` above pins the transaction's OWN original role into this list
  // regardless of which branch the filter currently names, so the filter being set to AMIN with
  // nobody holding an AMIN role still shows exactly one option — the original TMM role — with no
  // visible sign it isn't an AMIN role at all. Picking it (reasonably, since it's the only thing
  // offered) silently resubmits the SAME role: nothing re-mints, `branchId` doesn't move, and the
  // Cabang filter's own on-screen value (AMIN) disagrees with what's actually stored (TMM) with no
  // error anywhere. Fixed at the LABEL, not by hiding the option — hiding it would blank the
  // field's own display for the already-existing "a role doesn't match its own anchor" edge case
  // (`SalespersonPicker`'s `selected` is looked up from this exact same array), and re-selecting
  // the SAME role is a genuine no-op under the person/branch split below (Fix 2) regardless of
  // which branch the filter happens to be showing. Applies to BOTH branches of this ternary — a
  // Pusat anchor has the identical latent case (a pinned Cabang role shown under a Pusat-filtered
  // list), not just the Cabang-filter one this was first reported against.
  const filterBranchId = needSalesBranchFilter ? (salesBranchFilter || null) : null;
  const foreignBranchNote = (s) => {
    if (!isCurrentRole(s) || s.branchId === filterBranchId) return null;
    const label = s.branchId === null ? "Pusat" : (resolveBranchName(branches, s.branchId) || "Cabang");
    return ` — peran di ${label}`;
  };
  // `salespersonId` must be a genuine member of `filteredSalespeople` — not merely truthy — so
  // a value that survived from a stale default (or any future seeding change that reintroduces
  // a mismatch) can never reach Simpan just because it happens to be a real salesperson id
  // somewhere in the system. This is the explicit, structural version of Part 1's seeding fix:
  // Part 1 keeps the common case from ever mismatching; this is what keeps a mismatch from
  // mattering even if it happens anyway.
  const salespersonSelected = filteredSalespeople.some((s) => s.id === salespersonId);
  // Change 1: the two reasons `needSalesBranchFilter && !salesBranchFilter` can be true in EDIT
  // mode. The common one is "genuinely no branch chosen yet" (create, or an edit where the user
  // has cleared the Cabang select by hand) — `salespersonRoleMissing` is false there, and the
  // generic "pick a branch" message applies. The other, now much narrower since `salesBranchFilter`'s
  // own seed falls back to `initial?.branchId` (top of this file): the transaction's own STORED
  // salesperson role no longer existing in `salespeople` at all (checked against the full,
  // unfiltered prop — not `visibleSalespeople`, which would still find a merely-deactivated-but-
  // extant role) USED TO leave `salesBranchFilter` blank even when the transaction's real
  // `branchId` was still set — that bug is fixed, so an ordinary deleted-role transaction with a
  // real stored `branchId` no longer disables this field at all; the Sales picker just renders
  // normally, pre-filtered to that real branch, with `salespersonId` unresolved until the user
  // picks someone (see `salesHint`'s own "Pilih sales." case below). This block, and
  // `SALES_ROLE_UNRESOLVABLE`, are only still reachable for data old enough to predate the branch
  // feature entirely — a pre-feature CABANG transaction with `branchId: null` (root doc's own
  // "left null rather than guessed" rule) whose role has ALSO since been deleted, where there is
  // truly no branch anywhere to fall back to. Saying "pick a branch" there would still be false —
  // nothing was ever recorded to pick. Never true on create (no `initial`).
  const salespersonRoleMissing = isEdit && !!initial?.salespersonId &&
    !salespeople.some((s) => s.id === initial.salespersonId);
  // The one message explaining why Sales is blocking Simpan, in priority order: (1) no
  // salespeople exist ANYWHERE — no branch choice would help, so this pre-empts the ordinary
  // "pick a branch" hint entirely; on Pusat (no branch filter at all), that leaves only "no
  // salespeople"/"pick one" — (2)/(3) below (the branch-specific hints) never apply there;
  // (2) no branch chosen yet (Fix 2's original hint, Cabang only); (3) a branch/location chosen
  // whose OWN salespeople list is empty, even though salespeople exist elsewhere — a different
  // problem than (1), so it gets its own wording naming the gap AND that a matching name can
  // still be added right here (the picker's own inline add-role offer, see SalespersonPicker.js);
  // (4) real options exist, but none of them is the currently held value. `null` once a genuine
  // selection is in place. Not shown at all unless `needSales`.
  const salesHint = !needSales ? null
    : visibleSalespeople.length === 0 ? { text: "Belum ada sales terdaftar — tambahkan dulu lewat \"Kelola Sales\".", red: true }
    : !needSalesBranchFilter
      ? (filteredSalespeople.length === 0
          ? { text: "Belum ada sales di Pusat — tambahkan dulu lewat \"Kelola Sales\", atau ketik nama untuk menambah peran di sini.", red: true }
          : !salespersonSelected ? { text: "Pilih sales.", red: false } : null)
    // Change 1: this used to also carry a "Pilih cabang dahulu." message here — now suppressed
    // (`null`) because the disabled Sales field itself states the reason directly, in the field,
    // per Change 1's own requirement of one sentence, not two duplicated ones. "Belum ada sales
    // terdaftar" above is untouched — that's a different, more fundamental problem than "no
    // branch chosen" and still needs saying even while the field is disabled for this reason too.
    : !salesBranchFilter ? null
    : filteredSalespeople.length === 0 ? { text: "Cabang ini belum punya sales — tambahkan dulu lewat \"Kelola Sales\", atau ketik nama untuk menambah peran di sini.", red: true }
    : !salespersonSelected ? { text: "Pilih sales.", red: false }
    : null;
  // The picker's own location context for the inline add-role offer (SalespersonPicker.js) —
  // display label + the branchId a newly-added/reactivated role there should carry.
  const salesLocationLabel = needSalesBranchFilter ? (branches.find((b) => b.id === salesBranchFilter)?.name || "cabang ini") : "Pusat";
  const salesLocationBranchId = needSalesBranchFilter ? (salesBranchFilter || null) : null;

  // Contacts/salespeople link-up, Part 2 — contact-driven Sales resolution.
  //
  // `branchIdOrPending`: the argument `resolveContactSalespersonRoles` needs — `null` for Pusat,
  // a real branch id once one is chosen for a Cabang anchor, or `undefined` ("pending") when the
  // anchor is Cabang but no branch has been picked yet, meaning there isn't enough information
  // to resolve anything (also `undefined`, harmlessly, whenever `!needSales` — the function
  // below's own `!needSales` guard is what actually skips resolution in that case).
  const currentSalesBranchIdOrPending = () =>
    !needSales ? undefined : (needSalesBranchFilter ? (salesBranchFilter || undefined) : null);

  // Resolves the Sales field from a Contact — the one place `pickContact`, `handleLocationChange`,
  // and the Cabang branch-select's own onChange all funnel through, so a contact pick, a
  // location toggle, and a branch pick all re-resolve the exact same way. Real example from live
  // data: contact "PT Chang" names Albert; Albert holds only a Cabang role; the sale is at
  // Pusat — without this, the picker used to silently offer a different person (Budi) instead;
  // now it offers Albert with the exact "belum ada peran di Pusat" add-role path
  // `SalespersonPicker` already has (see `pinnedAddRoleCandidates` in the JSX below), never a
  // silently different person.
  //
  //  - `!needSales` (PEMBELIAN) or `isTransfer`: never resolves at all. PEMBELIAN has no Sales
  //    field; TRANSFER's own `contactId` is always the app-managed INTERNAL branch stand-in
  //    (`resolveTransferContact`, App.js) — never a real, user-linked contact, and its
  //    `personIds` is always empty by construction — so treating it as a real signal would
  //    misread every edit-mode TRANSFER as "contact has no people," silently clearing an
  //    already-valid, untouched Sales pick on nothing more than an Arah toggle round-trip (even
  //    a re-click of the CURRENTLY-active button, since `handleLocationChange` below has no
  //    "did this actually change" guard of its own). TRANSFER's own `ContactPicker` never
  //    renders in the first place (`pickContact` is structurally unreachable there), so this
  //    guard exists specifically for the OTHER two call sites below (`handleLocationChange`, the
  //    Cabang branch-select's own onChange), which DO still run for TRANSFER.
  //  - `branchIdOrPending === undefined` (a Cabang anchor with no branch chosen yet): nothing CAN
  //    be resolved yet — leaves `salespersonId` untouched (a real pick, manual or previously
  //    autofilled, is never blanked out just because we can't yet tell whether it's still valid)
  //    but clears any stale narrowing/gap banner, since that referenced a branch context that no
  //    longer applies.
  //  - otherwise ALWAYS clears then re-applies, unconditionally — a manually chosen salesperson
  //    is overwritten too; the contact (or, for `noPeople`/no contact selected at all, the
  //    absence of one) is what the salesperson follows from. "single" autofills and records it
  //    as such (the "terisi otomatis" hint below reads `autofilledRoleId`); "multiple"/
  //    "noRoleHere" clear the pick and surface an explanation; "noPeople" just clears, no
  //    explanation needed.
  const applyContactRoleResolution = (contact, branchIdOrPending) => {
    if (!needSales || isTransfer) return;
    if (branchIdOrPending === undefined) { setContactRoleNarrowing(null); return; }
    const resolution = resolveContactSalespersonRoles(persons, salespeople, contact?.personIds, branchIdOrPending);
    setContactRoleNarrowing(
      resolution.kind === "multiple" || resolution.kind === "noRoleHere" ? resolution : null
    );
    if (resolution.kind === "single") {
      setSalespersonId(resolution.role.id);
      setAutofilledRoleId(resolution.role.id);
    } else {
      setSalespersonId("");
      setAutofilledRoleId(null);
    }
    // Change 3: returns the resolution so `pickContact` (the only call site that represents an
    // actual contact change) can build its own "what changed" note from it — `handleLocationChange`
    // and the branch-select's onChange ignore this return value entirely.
    return resolution;
  };
  // Bug fix (manual-testing report, "the serious one"): every MANUAL/explicit real-value
  // selection — an ordinary pick, confirming the "belum ada peran" add-role offer, or completing
  // "+ Tambah sales baru…" — must also clear any leftover `contactRoleNarrowing` from a PRIOR
  // resolution pass. Without this, a resolved "noRoleHere"/"multiple" narrowing stayed on screen
  // forever once its own gap was filled: `pinnedAddRoleCandidates`/`salespeopleForPicker` (both
  // derived from `contactRoleNarrowing`) kept showing the now-stale greyed name + "Tambahkan
  // peran" button even after a role for that exact person had just been created and selected —
  // clicking it again then hit `addSalesperson`'s own "an ACTIVE role already exists here"
  // refusal and surfaced a false "tidak dapat ditambahkan" error for an action that, in truth,
  // had already succeeded moments earlier. Every real selection resolves whatever gap or
  // ambiguity `contactRoleNarrowing` was describing, so it always clears here, unconditionally —
  // not just for the specific "noRoleHere" case that exposed this.
  const commitSalespersonSelection = (id) => {
    setSalespersonId(id || "");
    setAutofilledRoleId(null);
    setContactRoleNarrowing(null);
    // Change 3: a deliberate manual re-pick supersedes whatever the last contact-driven swap
    // note was describing — keeping it around would describe a value the user just overrode.
    setSalespersonSwapNote(null);
  };
  // Toggling Lokasi/Arah changes which role is even valid (a Pusat role is never valid for a
  // Cabang anchor and vice versa, per this file's own location-filtering fix above) — clear a
  // selection that the NEW anchor would immediately reject, rather than leaving a stale id
  // selected that the now-differently-filtered picker can no longer resolve or display. Mirrors
  // the existing "Cabang" branch-select's own onChange, which already clears a now-mismatched
  // pick the same way; this is the same idea, one level up, for the anchor itself.
  const handleLocationChange = (newLocation) => {
    // Change 3: an anchor change, not a contact change — see the identical comment on the
    // Cabang branch-select's own onChange for why this clears rather than builds a note.
    setSalespersonSwapNote(null);
    setSalespersonId((id) => {
      if (!id) return id;
      const sp = salespeople.find((s) => s.id === id);
      if (!sp) return id;
      const validForNewAnchor = newLocation === "PUSAT"
        ? sp.branchId === null
        : sp.branchId === salesBranchFilter; // Cabang: must match the currently-chosen branch, if any
      return validForNewAnchor ? id : "";
    });
    // Bug fix (manual-testing report): a Contact belongs to a specific list (PUSAT/CABANG) that
    // must match the transaction's own anchor — exactly the same "a picked value can silently
    // stop being valid when the anchor changes" concern the salesperson clearing above already
    // handles. Without this, a PUSAT contact stayed selected (and visibly shown) after switching
    // to Cabang: `pickableContacts`' own "keep the transaction's OWN already-referenced contact
    // visible" exception (meant for EDIT mode re-opening an old, list-mismatched transaction —
    // see that memo's own comment in this file) was silently rescuing a plain CREATE-mode
    // mismatch too, since nothing here ever re-checked the pick after a location change. Clears
    // visibly — buyerName/buyerAddress (both derived FROM this specific contact) clear with it,
    // so nothing stale lingers on screen once the contact itself is gone.
    const currentContact = contactId ? contacts.find((c) => c.id === contactId) : null;
    const contactStillValid = !!currentContact && currentContact.list === newLocation;
    if (currentContact && !contactStillValid) {
      setContactId(null);
      setBuyerName("");
      setBuyerAddress("");
      setSelectedAddressId(null);
    }
    setLocation(newLocation);
    // Part 2: re-resolve Sales from the contact that will actually remain selected after this
    // switch (`null` when it just got cleared above) — a superset of the manual-pick check just
    // above (it also autofills when the new anchor makes exactly one of the contact's people
    // newly valid, not just clears). Only runs when a contact was ACTUALLY involved
    // (`currentContact` truthy) — when none was ever picked, this must stay a no-op, or it would
    // unconditionally clear a manually-picked salesperson on every toggle even for a plain
    // PENJUALAN/PEMBELIAN transaction that never had a contact-driven resolution in play at all,
    // overriding the "keep if still valid" logic the functional update above already provides.
    if (currentContact) {
      const branchIdOrPending = !needSales ? undefined : (newLocation === "CABANG" ? (salesBranchFilter || undefined) : null);
      applyContactRoleResolution(contactStillValid ? currentContact : null, branchIdOrPending);
    }
  };
  // Contacts/salespeople link-up, Part 2 — the picker's own narrowed list + pinned add-role
  // offer + explanatory hints, all derived from `contactRoleNarrowing`.
  //
  // "multiple": the Sales picker itself is narrowed to just the contact's own valid roles — a
  // further restriction on top of `filteredSalespeople`'s existing location/branch scoping, not
  // instead of it (`SalespersonPicker`'s own contract already requires its `salespeople` prop to
  // arrive pre-scoped by the host — see that component's own header comment).
  const salespeopleForPicker = contactRoleNarrowing?.kind === "multiple"
    ? (() => {
        const ids = new Set(contactRoleNarrowing.roles.map((r) => r.id));
        return filteredSalespeople.filter((s) => ids.has(s.id));
      })()
    : filteredSalespeople;
  // "noRoleHere": every one of the contact's people who has no role at this location — pinned
  // into `SalespersonPicker` so its EXISTING "belum ada peran di ..." add-role offer shows
  // without requiring the user to first type a matching name (see that component's own
  // `pinnedAddRoleCandidates` prop). Never a silently different person offered instead.
  const pinnedAddRoleCandidates = contactRoleNarrowing?.kind === "noRoleHere"
    ? contactRoleNarrowing.persons
    : [];
  const contactRoleHint = contactRoleNarrowing?.kind === "multiple"
    ? "Kontak ini terhubung ke beberapa sales — dipersempit ke daftar di bawah, pilih salah satu."
    : contactRoleNarrowing?.kind === "noRoleHere"
      // The name itself shows greyed in the Sales field (SalespersonPicker's own "unresolved"
      // display) — this hint states plainly that it ISN'T a real pick yet and points at the
      // adjacent "Tambahkan peran" button that turns it into one, so a greyed name never reads
      // as silently decided.
      ? `${contactRoleNarrowing.persons.map((p) => p.name).join(", ")} belum punya peran di ${salesLocationLabel} — bukan pilihan yang sah sampai peran ditambahkan lewat tombol di bawah.`
      : null;
  // "single": a small, visible confirmation that the current value came from the contact, not a
  // manual pick — never held invisibly, and gone the instant the value moves away from the
  // exact role this pass filled in (a manual re-pick, or a later resolution pass).
  const autofillHint = autofilledRoleId && autofilledRoleId === salespersonId
    ? "Sales terisi otomatis dari kontak yang dipilih."
    : null;
  // Multi-branch cabang feature + post-launch Fix 3: the two cases with no salesperson to
  // derive a branch from — a Pusat→Cabang transfer (needSales is false there — anchor is
  // PUSAT), and a Cabang-anchored PEMBELIAN (PEMBELIAN never has a salesperson at all) — both
  // get a plain, explicitly-picked required branch instead.
  const needBranchPicker = (isTransfer && location === "PUSAT") || (isPembelian && location === "CABANG");
  // A pre-feature Cabang Pembelian stored with no branch may be re-saved still empty (picking one
  // is optional there) — every other case requires a branch, as before.
  const pembelianBranchOptional = isEdit && isPembelian && !initial?.branchId;
  // Payment-correction rework: runs unconditionally now, in both modes — this alone is the
  // whole implementation of "the toggle blocks Simpan until the real rows agree with it,"
  // reusing the exact same messages create mode already showed ("Lunas harus berjumlah tepat
  // sama dengan total" / "Belum Lunas harus berjumlah kurang dari total").
  const paymentsCheck = validatePayments(payments, total, effectiveIsLunas ? "LUNAS" : "BELUM_LUNAS");
  // Cost items (Brief 1): continuous validation, same shape as paymentsCheck — a row added
  // via "+ Biaya"/"+ Tagihan" starts blank/invalid (description "") and blocks Simpan until
  // filled in, mirroring how an added payment row blocks Simpan until its amount matches.
  const costItemsCheck = validateCostItems(costItems, txnType);
  // Payment-correction rework fix: within one Edit Transaksi save, two brand-new rows (no
  // existing `id`) sharing one method are ambiguous — both get stamped with the SAME `at`
  // timestamp server-side, indistinguishable even with the timestamp now shown elsewhere (see
  // TransactionDetailModal.js/BayarTagihanModal.js). A new row sharing a method with an
  // EXISTING row (real id, its own real distinct historical `at`) is NOT ambiguous and stays
  // allowed — the ordinary "paid Tunai again, days later" case. Edit-mode only: in create mode
  // every row is "new" by definition (nothing persisted yet), so this rule doesn't apply there.
  const newRowMethodCounts = useMemo(() => {
    const counts = new Map();
    if (!isEdit) return counts;
    for (const p of payments) {
      if (p.id || !p.method) continue; // an unpicked method can't duplicate anything yet
      counts.set(p.method, (counts.get(p.method) || 0) + 1);
    }
    return counts;
  }, [isEdit, payments]);
  const hasDuplicateNewMethod = Array.from(newRowMethodCounts.values()).some((c) => c > 1);
  // paidAt duplicate-prevention feature: a row is flagged when its method+paidAt exactly match
  // ANY other row currently in `payments` — every already-persisted row (edit mode) plus every
  // other row in this same form session, both modes. Combining via `combineDateTime` here (not
  // reading any stale `p.paidAt`) means this always checks the exact value `buildDraft` would
  // actually submit. Editing an EXISTING row's date/time into a collision is caught the same way
  // a brand-new row is, since both live in this one `payments` array and this recomputes every
  // render.
  const paymentsWithPaidAt = useMemo(() => payments.map((p) => ({
    method: p.method, paidAt: combineDateTime(p.paidAtDate, p.paidAtTime),
  })), [payments]);
  const duplicatePaymentIndexes = useMemo(() => {
    const set = new Set();
    paymentsWithPaidAt.forEach((cand, i) => {
      const others = paymentsWithPaidAt.filter((_, idx) => idx !== i);
      if (hasDuplicatePaymentAgainst(cand, others)) set.add(i);
    });
    return set;
  }, [paymentsWithPaidAt]);
  const hasDuplicatePayment = duplicatePaymentIndexes.size > 0;
  const canSubmit = lines.length > 0 && lines.every((l) => l.internalCode && Number(l.qty) > 0) &&
    (isTransfer || !!contactId) &&
    (!needSales || salespersonSelected) &&
    (!needBranchPicker || !!branchId || pembelianBranchOptional) &&
    (!isPembelian || !!supplierInvoiceNo.trim()) &&
    (effectiveIsLunas || dueDaysNum != null) &&
    totalCheck.ok &&
    paymentsCheck.ok &&
    !hasDuplicateNewMethod &&
    !hasDuplicatePayment &&
    costItemsCheck.ok &&
    biayaLainCheck.ok &&
    allSplitsValid &&
    !barangMessage && !tagihanMessage &&
    !barangOverAllocMessage && !tagihanOverAllocMessage;

  // Read-only preview of what saving right now would do to stock — reuses the REAL engine
  // (utils/txnStock.js, unchanged) rather than re-deriving the math, so this can never drift
  // from what the write path actually does. On edit, reverses the transaction's OLD effect
  // first, exactly like App.editTransaction does, so e.g. a PEMBELIAN edit that reduces qty
  // correctly previews as potentially going negative even though PEMBELIAN "only adds".
  const stockField = location === "CABANG" ? "stokCabang" : "stokPusat";
  const preview = useMemo(() => {
    const previewLines = lines
      .filter((l) => l.internalCode && Number(l.qty) > 0)
      .map((l) => ({ internalCode: l.internalCode, qty: Number(l.qty) || 0 }));
    const previewTxn = {
      type: txnType, location,
      transferDirection: isTransfer ? (location === "PUSAT" ? "PUSAT_TO_CABANG" : "CABANG_TO_PUSAT") : null,
      lines: previewLines,
    };
    let baseData = { items };
    if (isEdit && initial) baseData = applyTxnStockDelta(baseData, initial, -1);
    const afterData = applyTxnStockDelta(baseData, previewTxn, +1);
    return {
      before: new Map(baseData.items.map((it) => [it.internalCode, it])),
      after: new Map(afterData.items.map((it) => [it.internalCode, it])),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, location, txnType, isTransfer, items, isEdit, initial]);

  const lineStock = (l) => ({
    before: Number(preview.before.get(l.internalCode)?.[stockField]) || 0,
    after: Number(preview.after.get(l.internalCode)?.[stockField]) || 0,
  });

  // The item's REAL, currently-persisted stock — read straight off the `items` prop, never off
  // `preview.before`. In edit mode `preview.before` is a synthetic baseline (current stock with
  // the transaction's OWN old effect already reversed out), which is the right thing to diff
  // against for computing `preview.after` itself, but the WRONG thing to compare against for
  // "did this action make stock worse" (see the `flagged` build in `submit()` below for why that
  // distinction is load-bearing). In create mode this is identical to `preview.before` (nothing
  // to reverse), so this only ever changes behavior for an edit.
  const liveStock = (l) => Number(items.find((it) => it.internalCode === l.internalCode)?.[stockField]) || 0;

  // ── Substitute items (section G, 2026-10-12) ─────────────────────────────────────────────
  // On a sale or transfer, a line that takes its bucket below zero AND makes the real stock worse
  // (the exact condition the save-time StockWarningModal uses) offers the item's sibling links
  // ("Saudara") that have stock in the same bucket — the transfer's SOURCE bucket, since
  // `location` is the source. Never on PEMBELIAN (it only adds stock). Most stock first; a
  // sibling already on another line is skipped. "Pakai" keeps what the original item can cover
  // and moves only the shortfall to the substitute, on a new line right after it — capped at the
  // substitute's own stock, so a big shortfall can be split over several substitutes (Chalton's
  // option b). The new line copies the typed price (still editable) and carries a FORM-ONLY
  // `substituteFor` hint ("Pengganti untuk …"); it is never saved — `submit()` builds lines
  // from explicit fields only. Purely a suggestion: ignoring it saves exactly as before.
  const isSaleSide = txnType === "PENJUALAN" || isTransfer;
  const stockAfterOf = (code) => Number(preview.after.get(code)?.[stockField]) || 0;
  const substituteSuggestions = (l) => {
    if (!isSaleSide || !l.internalCode) return [];
    const after = lineStock(l).after;
    if (!(after < 0 && after < liveStock(l))) return [];
    const onLines = new Set(lines.map((x) => x.internalCode).filter(Boolean));
    return siblingsOf({ siblingLinks }, l.internalCode)
      .filter((code) => !onLines.has(code) && byCode.has(code))
      .map((code) => ({ code, name: byCode.get(code).name || code, stock: stockAfterOf(code) }))
      .filter((s) => s.stock > 0)
      .sort((a, b) => b.stock - a.stock);
  };
  const applySubstitute = (i, sub) => {
    const l = lines[i];
    const qty = Number(l.qty) || 0;
    const shortfall = Math.min(qty, -lineStock(l).after);
    const moveQty = Math.min(shortfall, sub.stock);
    if (!(moveQty > 0)) return;
    const keep = qty - moveQty;
    const newLine = { internalCode: sub.code, name: sub.name, qty: moveQty, unitPrice: l.unitPrice, collyNo: null, substituteFor: l.internalCode };
    setLines((ls) => {
      const out = [...ls];
      if (keep > 0) {
        out[i] = { ...ls[i], qty: keep };
        out.splice(i + 1, 0, newLine);
      } else {
        out[i] = newLine; // nothing of the original is available — the whole line becomes the substitute
      }
      return out;
    });
  };

  // Duplicate-item warning (Phase T2b-fix-3): a transaction MAY legitimately repeat the same
  // item across several lines (e.g. a real invoice splitting one part across two colly
  // numbers) — this is informational only, never a block. Matched on internalCode, never
  // name (two different items can share a name). Counted once per render rather than an
  // O(n²) per-line scan.
  const duplicateCodeCounts = useMemo(() => {
    const counts = new Map();
    for (const l of lines) {
      if (!l.internalCode) continue;
      counts.set(l.internalCode, (counts.get(l.internalCode) || 0) + 1);
    }
    return counts;
  }, [lines]);

  const buildDraft = (cleanLines) => {
    const draft = {
      type: txnType, location, date, time,
      lines: cleanLines, isLunas: effectiveIsLunas, dueDate: effectiveIsLunas ? null : dueDate,
    };
    // Two-bucket payment split: the two per-bucket intents are sent ONLY when both toggles are
    // actually real (`bothBucketsLive`) — NOT merely whenever the split UI happens to be showing
    // (`showBucketUI`, which also covers state 2, where neither toggle is real). This is required,
    // not cosmetic: `App.js`'s `editTransaction` gates its own `validateBucketCompleteness` calls
    // PER-FIELD (`patch.barangLunas !== undefined`), independently of its own recomputed owed
    // signs — sending a stale value here once `bothBucketsLive` is false would make it run
    // Check2-Barang against `barangOwed(afterTxnLike)` as the reference, reintroducing the exact
    // wrongly-scoped check this redesign exists to remove, just server-side instead of in the UI.
    if (bothBucketsLive) {
      draft.barangLunas = barangLunas;
      draft.tagihanPotonganLunas = tagihanPotonganLunas;
    }
    if (isTransfer) {
      draft.transferDirection = location === "PUSAT" ? "PUSAT_TO_CABANG" : "CABANG_TO_PUSAT";
      draft.salespersonId = salespersonId;
      // Only read server-side for the Pusat→Cabang direction (needBranchPicker) — a
      // Cabang→Pusat transfer's branch is derived from the salesperson instead.
      draft.branchId = branchId || null;
      // contactId / buyerBlock: resolved server-side from the reserved branch contact.
    } else if (isPembelian) {
      draft.contactId = contactId;
      draft.supplierInvoiceNo = supplierInvoiceNo.trim();
      // Post-launch fix (Fix 3): always set (mirroring the isTransfer branch's own
      // always-set style above) so `patch.branchId` is never `undefined` on an edit
      // resubmission — keeps invariant 19's `!== undefined` guard well-defined for PEMBELIAN.
      draft.branchId = location === "CABANG" ? (branchId || null) : null;
    } else {
      draft.contactId = contactId;
      draft.salespersonId = salespersonId;
      draft.buyerBlock = { name: buyerName.trim(), address: buyerAddress.trim() };
    }
    // Payment-correction rework: sent in BOTH modes now. In edit mode a row carrying its real
    // `id` is a correction/unchanged row; a row with no `id` (added via "+ Tambah metode"
    // during this edit) is new — editTransaction stamps id/at for those server-side, mirroring
    // createTransaction's own stamping. A row the user removed via "✕" simply isn't in this
    // array, which editTransaction's diff reads as a deletion.
    draft.payments = payments.map((p) => ({
      ...(p.id ? { id: p.id } : {}),
      amount: Number(p.amount) || 0, method: p.method, methodNote: p.methodNote,
      paidAt: combineDateTime(p.paidAtDate, p.paidAtTime),
      // Two-bucket payment split — Brief 2: gate purely on the ROW's OWN real current typeof,
      // never on `showBucketUI` (a blanket `showBucketUI && Number(...)||0` would silently
      // send {splitBarang:0, splitTagihanPotongan:0} for an untouched FROZEN row whenever the
      // section merely happens to be visible — editTransaction's own `typeof === "number"`
      // resolution would then treat that as "explicitly supplies both" and overwrite a
      // legitimate 100%-Barang row with an explicit 0/0 split). A no-bucket creation, and an
      // untouched frozen row in edit mode, both correctly omit these fields entirely.
      ...(typeof p.splitBarang === "number" && typeof p.splitTagihanPotongan === "number"
        ? { splitBarang: p.splitBarang, splitTagihanPotongan: p.splitTagihanPotongan }
        : {}),
    }));
    // Cost items (Brief 1): same id-present-means-correction / id-absent-means-new
    // convention as `draft.payments` above.
    draft.expenses = biayaLain.map((r) => ({
      ...(r.id ? { id: r.id } : {}),
      category: r.category, paidTo: r.paidTo, amount: r.amount, note: r.note,
    }));
    draft.costItems = costItems.map((c) => ({
      ...(c.id ? { id: c.id } : {}),
      description: c.description, amount: Number(c.amount) || 0, type: c.type,
    }));
    return draft;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const cleanLines = lines.map((l) => ({
      internalCode: l.internalCode,
      name: l.name,
      qty: Number(l.qty),
      unitPrice: Number(l.unitPrice) || 0,
      collyNo: l.collyNo !== "" && l.collyNo != null ? String(l.collyNo) : null,
    }));
    const draft = buildDraft(cleanLines);

    // Bug fix ("warn before abandoning a number"): mirrors App.js's own `editTransaction`
    // re-mint gate (`personChanged && branchChanged`, see that function's own header comment)
    // exactly, computed client-side so the user can be told BEFORE the save — the write-path
    // guard still runs server-side regardless, this is purely advisory, same "speed bump, not a
    // wall" shape StockWarningModal already uses. Scoped to `isEdit && needSalesBranchFilter` —
    // the identical scope App.js's own gate uses (`anchor !== "PUSAT"`, sales-bearing types
    // only): a create has no `before` to compare against, and Pusat/PEMBELIAN never re-mint on
    // edit at all. Never fires on a same-person branch move (nothing is abandoned — the number
    // simply stays, per the person/branch split in App.js) or a same-branch person reassignment
    // (no re-mint happens there either — pre-existing, documented, deliberately untouched).
    if (isEdit && needSalesBranchFilter) {
      const currentSp = salespeople.find((s) => s.id === salespersonId);
      const beforeSp = salespeople.find((s) => s.id === initial?.salespersonId);
      const personChanged = !!currentSp && currentSp.personId !== (beforeSp ? beforeSp.personId : null);
      const branchChanged = !!currentSp && currentSp.branchId !== (initial?.branchId ?? null);
      if (personChanged && branchChanged) {
        setPendingDraft(draft);
        setPendingRemintWarning({
          oldName: beforeSp ? resolveSalespersonName(persons, beforeSp) : "sales sebelumnya",
          newName: resolveSalespersonName(persons, currentSp) || "sales baru",
          oldInvoiceNumber: initial?.invoiceNumber || "—",
        });
        return;
      }
    }

    proceedToStockCheck(draft);
  };

  // Peringatan Stok (item 7): a speed bump, never a block — negative stock stays legal
  // everywhere (invariant 2). Applies to all three types (a PEMBELIAN edit that reduces
  // qty can drive stock negative too — `preview` already accounts for that).
  //
  // **Bug fix — the condition used to be just `after < 0`.** That fired on ANY line ending up
  // negative, including a PEMBELIAN line that only IMPROVES an already-negative balance (e.g.
  // -1.707 -> -1.607 while buying) — the direction of the change was never checked, only the
  // final sign. Now: `after < 0 && after < before`, where `before` is `liveStock(l)` — the
  // item's REAL, currently-persisted stock, NOT `lineStock(l)`'s own `before`
  // (`preview.before`). That distinction is load-bearing, not cosmetic: in edit mode
  // `preview.before` is a synthetic baseline with THIS SAME transaction's old effect already
  // reversed out, so a PEMBELIAN line's own preview delta is always a positive addition
  // (`preview.after >= preview.before` unconditionally, since a PEMBELIAN line only ever adds
  // its own qty) — comparing against `preview.before` would mean a PEMBELIAN could NEVER
  // trigger this warning, even for the one case the old code's broad "applies to all three
  // types" comment above was written for: editing an existing purchase down to a smaller qty,
  // which genuinely subtracts stock relative to what's really on hand right now. Comparing
  // `after` against the real, live `before` instead catches exactly that case (a 200-unit
  // purchase edited down to 20, with real current stock at 50, correctly still warns: 50 ->
  // -130) while correctly clearing the reported false positive (a brand-new purchase can only
  // ever raise `after` above the live `before`, so `after < before` is never true for it).
  //
  // Extracted into its own function (bug fix, "warn before abandoning a number") so the new
  // sales-remint warning's own "Tetap Lanjutkan" can re-enter this exact same check against the
  // already-built draft, instead of duplicating it — the two warnings can both fire in one save
  // (remint confirmed first, stock check still runs after), never racing or skipping either.
  const proceedToStockCheck = (draft) => {
    const flagged = draft.lines
      .map((l) => ({ ...l, before: liveStock(l), after: lineStock(l).after }))
      .filter((l) => l.after < 0 && l.after < l.before);
    if (flagged.length > 0) {
      setWarningLines(flagged);
      setPendingDraft(draft);
      return;
    }
    onSubmit(draft);
  };

  const proceedAnyway = () => {
    if (pendingDraft) onSubmit(pendingDraft);
    setPendingDraft(null);
    setWarningLines([]);
  };
  const cancelAndCorrect = () => {
    setPendingDraft(null);
    setWarningLines([]);
    // Form state is untouched — every field is exactly as the user left it.
  };

  const proceedPastRemintWarning = () => {
    const draft = pendingDraft;
    setPendingRemintWarning(null);
    setPendingDraft(null);
    if (draft) proceedToStockCheck(draft);
  };
  const cancelRemintWarning = () => {
    setPendingRemintWarning(null);
    setPendingDraft(null);
    // Form state is untouched — every field is exactly as the user left it, same contract as
    // cancelAndCorrect above.
  };

  // Address picker render-time derivations (JSX below). `selectedContact` is looked up fresh on
  // every render, same as `handleLocationChange`'s own `currentContact` a few hundred lines up —
  // deliberately NOT hoisted into a single shared memo, to keep this change's diff scoped to the
  // address feature rather than refactoring an existing, working lookup pattern.
  const selectedContact = contactId ? contacts.find((c) => c.id === contactId) || null : null;
  // Non-null only while `buyerAddress` still equals the exact address `selectedAddressId` names
  // — never once the user has typed past it (the input's own onChange below clears
  // `selectedAddressId` on every keystroke) or the contact itself changed underneath it.
  const selectedAddr = selectedContact?.addresses?.find((a) => a.id === selectedAddressId) || null;
  // Reopening an old transaction (edit, or a duplicate seeded from one): the frozen
  // `buyerBlock.address` may no longer match ANY of the contact's CURRENT addresses — the
  // contact could have been edited since. Fires only while the field still holds that exact
  // original value untouched (never after the user edits it here, and never for a fresh blank
  // create, where both sides are `""`) — a quiet, informational note, never a warning that
  // blocks anything or rewrites the field.
  const addressNoLongerMatchesContact =
    !selectedAddressId &&
    buyerAddress.trim() !== "" &&
    buyerAddress === (initial?.buyerBlock?.address || "") &&
    !!selectedContact &&
    (selectedContact.addresses || []).length > 0 &&
    !selectedContact.addresses.some((a) => a.text === buyerAddress);

  // Sales-clarity pass (Change 4 — naming an overridden salesperson). `autofillHint` only ever
  // says something while the field still holds the EXACT role the contact resolved to — the
  // instant the user overrides it with someone else, that green hint simply vanishes with
  // nothing else on screen explaining why. An absence is not a message: whenever a real contact
  // is selected, names at least one real person via `personIds`, and the CURRENTLY selected
  // salesperson's own `personId` isn't among them, name the contact's usual salesperson so the
  // override reads as a stated fact, not a silent discrepancy. The override itself stays fully
  // allowed — this never blocks Simpan — and the CONTACT is never written to; a stand-in
  // covering once is not a change of account ownership. Purely a per-render derivation, like
  // `autofillHint`/`contactRoleHint` just above — not its own piece of state — so it self-heals
  // the instant `salespersonId`/`contactId` genuinely change, INCLUDING on an edit form's mount
  // (an already-mismatched stored pair needs no interaction to surface this; nothing here is a
  // mutation of form state, only a display-time read of it). Explicitly excludes TRANSFER, same
  // as `applyContactRoleResolution`'s own guard — its `contactId` is always the app-managed
  // internal stand-in, whose `personIds` is always empty by construction, never a real signal.
  const selectedSalesperson = needSales && !isTransfer ? salespeople.find((s) => s.id === salespersonId) || null : null;
  const contactUsualSalespersonNames = (selectedContact?.personIds || [])
    .map((pid) => persons.find((p) => p.id === pid))
    .filter(Boolean)
    .map((p) => p.name);
  const salespersonOverrideHint = (
    !isTransfer && selectedContact && selectedSalesperson &&
    (selectedContact.personIds || []).length > 0 &&
    !selectedContact.personIds.includes(selectedSalesperson.personId) &&
    contactUsualSalespersonNames.length > 0
  ) ? `Sales kontak ini biasanya ${contactUsualSalespersonNames.join(", ")} — sales lain dipilih untuk transaksi ini.`
    : null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <form className="modal-box modal-box--wide" onSubmit={submit}>
        <h2 className="modal-title">
          {isEdit ? `Ubah Transaksi ${TYPE_LABEL[txnType]}` : `Transaksi ${TYPE_LABEL[txnType]} Baru`}
        </h2>
        {isEdit && !isPembelian && initial?.invoiceNumber && (
          <div className="page-sub mono" style={{ marginBottom: "var(--gap-lg)" }}>{initial.invoiceNumber}</div>
        )}
        {isEdit && isPembelian && initial?.supplierInvoiceNo && (
          <div className="page-sub mono" style={{ marginBottom: "var(--gap-lg)", color: "#555" }}>{initial.supplierInvoiceNo}</div>
        )}

        <div className="form-grid">
          <div className="form-row">
            <label className="form-label" htmlFor="txn-form-tanggal">Tanggal</label>
            <input id="txn-form-tanggal" className="form-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="txn-form-waktu">Waktu</label>
            <input id="txn-form-waktu" className="form-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <label className="form-label">{isTransfer ? "Arah" : "Lokasi"}</label>
          <div className="seg">
            <button type="button" disabled={isEdit} className={location === "PUSAT" ? "active" : ""} onClick={() => handleLocationChange("PUSAT")}>
              {isTransfer ? "Pusat → Cabang" : "Pusat"}
            </button>
            <button type="button" disabled={isEdit} className={location === "CABANG" ? "active" : ""} onClick={() => handleLocationChange("CABANG")}>
              {isTransfer ? "Cabang → Pusat" : "Cabang"}
            </button>
          </div>
          {isEdit && (
            <p className="form-hint">
              {isTransfer ? "Arah tidak bisa diubah setelah transaksi dibuat." : "Lokasi tidak bisa diubah setelah transaksi dibuat."}
            </p>
          )}
        </div>

        {/* Change 2 (Sales-clarity pass): the Sales-branch filter sits directly under Lokasi/Arah,
            ahead of Pembeli/Penjual and Nama/Alamat Tercetak — deliberately, not just visually.
            Sales depends on this branch; putting it last (its old position, right above Sales)
            let someone fill in the contact/buyer fields first and only then discover Sales was
            still blocked. Tab order follows this same move, on purpose. */}
        {needSalesBranchFilter && (
          <div className="form-row">
            {/* Relabeled back to plain "Cabang" on direct user instruction (components/
                CLAUDE.md's "Fix 4" entry has the full history). This is NOT the transaction's
                own branch field (that's `needBranchPicker`'s own select below, labeled
                "Cabang"/"Cabang Tujuan" — left alone, that one IS the branch); this one only
                narrows which salespeople are offered. The label collision Fix 4 existed to
                prevent is NOT resolved by this revert — both selects can still render, under the
                literally identical label, at different times on this same form (never
                simultaneously, since the two conditions are mutually exclusive — see
                `needBranchPicker`/`needSalesBranchFilter`'s own definitions). Relabeling here is
                a deliberate instruction, not a claim the collision risk is gone. */}
            <label className="form-label" htmlFor="txn-form-cabang">Cabang</label>
            <select id="txn-form-cabang" className="form-select" value={salesBranchFilter}
              onChange={(e) => {
                const val = e.target.value;
                setSalesBranchFilter(val);
                // Clear the now-invalid salesperson selection if it belongs to a different branch.
                setSalespersonId((id) => {
                  const sp = salespeople.find((s) => s.id === id);
                  return sp && sp.branchId === val ? id : "";
                });
                // Change 3: an anchor-context change, not a contact change — the swap note is
                // scoped to pickContact only, so clear any leftover note rather than let it
                // describe a value this branch pick may have just moved away from.
                setSalespersonSwapNote(null);
                // Part 2: re-resolve from the currently selected contact now that the branch is
                // known (or has changed) — a superset of the manual-pick clear just above.
                if (contactId) {
                  const c = contacts.find((x) => x.id === contactId) || null;
                  applyContactRoleResolution(c, val || undefined);
                }
              }}>
              <option value="">— pilih cabang —</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            {!isEdit && !salesBranchFilter && <div className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</div>}
          </div>
        )}

        {needBranchPicker && (
          <div className="form-row">
            <label className="form-label" htmlFor="txn-form-cabang">{isTransfer ? "Cabang Tujuan" : "Cabang"}</label>
            {/* Invariant 19's second exception: a Cabang Pembelian's branch is editable (nothing
                derives from it — see App.js's editTransaction). A Transfer's Cabang Tujuan stays
                locked. Once a Pembelian has a branch the empty option is gone, so it can be moved
                but never blanked; a pre-feature one with none may stay empty (pembelianBranchOptional). */}
            <select id="txn-form-cabang" className="form-select" value={branchId} disabled={isEdit && isTransfer}
              onChange={(e) => setBranchId(e.target.value)}>
              {!(isEdit && initial?.branchId) && <option value="">— pilih cabang —</option>}
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            {!isEdit && !branchId && <div className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</div>}
            {/* Disabled-control sweep (manual-testing report): the stated reason for the
                invariant-19 lock, mirroring the Lokasi/Arah toggle's own wording. */}
            {isEdit && isTransfer && (
              <p className="form-hint">Cabang Tujuan tidak bisa diubah setelah transaksi dibuat.</p>
            )}
          </div>
        )}

        {isTransfer ? (
          <div className="form-row">
            <label className="form-label">Pihak</label>
            <div className="readonly-stock">
              {RESERVED_CONTACT_NAME[location === "PUSAT" ? "PUSAT_TO_CABANG" : "CABANG_TO_PUSAT"]}
            </div>
            <p className="form-hint">Transfer selalu tercatat ke kontak internal cabang tujuan.</p>
          </div>
        ) : (
          <div className="form-row">
            <label className="form-label" htmlFor="txn-form-pihak">{isPembelian ? "Penjual" : "Pembeli"}</label>
            <ContactPicker id="txn-form-pihak" value={contactId} contacts={pickableContacts} onChange={pickContact}
              onCreateContact={onCreateContact}
              createList={location} persons={persons} onCreateContactFull={onCreateContactFull} />
          </div>
        )}

        {!isPembelian && !isTransfer && (
          <>
          <div className="form-grid">
            <div className="form-row">
              <label className="form-label" htmlFor="txn-form-nama-tercetak">Nama Tercetak</label>
              <input id="txn-form-nama-tercetak" className="form-input" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
            </div>
            <div className="form-row">
              <label className="form-label" htmlFor="txn-form-alamat-tercetak">Alamat Tercetak</label>
              {/* Real-browser report: the select and the input below it could show the exact
                  same text with nothing saying which is which — read as one control rendered
                  twice. Each now gets its own caption stating what it actually is, so the two
                  never look interchangeable: the select PICKS one of the contact's saved
                  addresses; the input IS what gets printed and is free to differ from any of
                  them. jsdom can't see this — verified in a real browser, not only by the DOM
                  assertions below. */}
              {selectedContact && (selectedContact.addresses || []).length > 1 && (
                <>
                  <p className="form-hint">Pilih salah satu alamat tersimpan milik kontak ini:</p>
                  <select className="form-select" value={selectedAddressId || ""}
                    onChange={(e) => pickAddress(e.target.value)}>
                    <option value="">— pilih alamat kontak —</option>
                    {selectedContact.addresses.map((a) => (
                      <option key={a.id} value={a.id}>{a.text}{a.isDefault ? " (default)" : ""}</option>
                    ))}
                  </select>
                  <p className="form-hint">
                    Alamat yang benar-benar tercetak — boleh diubah bebas, tidak harus sama
                    dengan pilihan di atas:
                  </p>
                </>
              )}
              <input id="txn-form-alamat-tercetak" className="form-input" value={buyerAddress}
                onChange={(e) => { setBuyerAddress(e.target.value); setSelectedAddressId(null); }} />
              {selectedAddr && (
                <p className="form-hint">Alamat terisi otomatis dari kontak yang dipilih.</p>
              )}
              {addressNoLongerMatchesContact && (
                <p className="form-hint">
                  Alamat ini sudah tidak cocok dengan alamat kontak yang ada sekarang — mungkin
                  berubah sejak transaksi ini dibuat.
                </p>
              )}
            </div>
          </div>
          {/* Rename (manual-testing report, issue 3): these fields are NOT a second copy of the
              Pembeli contact picker above — they're `buyerBlock`, the name/address that actually
              PRINT on the invoice and surat jalan, and may deliberately differ from the picked
              contact's own record (a different billing name, a corrected address for this one
              sale, etc.). The old "Nama Pembeli"/"Alamat Pembeli" labels read as duplicates of
              "Pembeli" right above them, which is exactly why they looked like leftovers. Named
              "Tercetak" (printed), not "di Invoice" — this same value also prints on Surat Jalan,
              not only the Invoice, so naming one specific document would undersell what it
              actually feeds. */}
          <p className="form-hint">
            Nama &amp; alamat yang tercetak di invoice dan surat jalan — boleh berbeda dari kontak
            yang dipilih di atas.
          </p>
          </>
        )}

        {needSales && (
          <div className="form-row">
            <label className="form-label" htmlFor="txn-form-sales">Sales</label>
            {/* Change 1: a CABANG anchor with no branch chosen yet has ZERO valid roles by
                construction (`filteredSalespeople` above) — the real `SalespersonPicker` never
                renders in this state. A plain disabled input stating why, in the field itself,
                replaces it entirely: no typing, no "+ Tambah sales baru", no "Tambahkan peran" —
                every one of those paths requires a real `salespeople`/`persons` match this state
                structurally cannot offer honestly. Two distinct messages for two distinct causes
                — see `salespersonRoleMissing`'s own comment above. */}
            {needSalesBranchFilter && !salesBranchFilter ? (
              <input id="txn-form-sales" className="form-input" disabled
                value={salespersonRoleMissing ? SALES_ROLE_UNRESOLVABLE : SALES_PICK_BRANCH_FIRST} />
            ) : (
              <SalespersonPicker id="txn-form-sales" value={salespersonId}
                salespeople={salespeopleForPicker}
                persons={persons}
                pinnedAddRoleCandidates={pinnedAddRoleCandidates}
                foreignBranchNote={foreignBranchNote}
                onChange={commitSalespersonSelection}
                onRequestCreate={(name) => setRoleFormRequest({ initialQuery: name })}
                locationLabel={salesLocationLabel}
                locationBranchId={salesLocationBranchId}
                onCreateSalespersonRole={onCreateSalespersonRole} />
            )}
            {/* Change 3: salespersonSwapNote describes a discrete past event (what the last
                contact change just did) — deliberately independent of contactRoleHint/
                autofillHint below it, NOT mutually exclusive with them: a contact change that
                resolves to a genuinely different single role fires BOTH the swap note AND
                autofillHint at once (the new value really is the fresh autofill); one that
                resolves to "multiple" fires BOTH the swap note AND contactRoleHint (the hint
                explains why Sales just went blank). Do not gate either of those two on
                `!salespersonSwapNote` — that was tried and tested wrong (two existing "Change 3"
                regression tests prove the coexistence; see `TransactionFormModal.test.js`).
                contactRoleHint/autofillHint ARE mutually exclusive with EACH OTHER, but only
                because the single underlying `applyContactRoleResolution` call that sets them
                always takes one branch or the other, never both — not because of anything here.
                Change 4's salespersonOverrideHint is the one hint that DOES need an explicit
                guard: it's a plain per-render derivation with no shared state keeping it apart
                from the other three, so each `!x &&` below is what makes "never fights an
                existing hint" structural rather than emergent — for THIS hint only. */}
            {salespersonSwapNote && <div className="form-hint" style={{ color: "var(--amber)" }}>{salespersonSwapNote}</div>}
            {contactRoleHint && <div className="form-hint">{contactRoleHint}</div>}
            {autofillHint && <div className="form-hint" style={{ color: "var(--green)" }}>{autofillHint}</div>}
            {!salespersonSwapNote && !contactRoleHint && !autofillHint && salespersonOverrideHint && (
              <div className="form-hint" style={{ color: "var(--amber)" }}>{salespersonOverrideHint}</div>
            )}
            {salesHint && (
              <div className="form-hint" style={salesHint.red ? { color: "var(--red)" } : undefined}>{salesHint.text}</div>
            )}
          </div>
        )}

        {roleFormRequest && (
          <SalespersonFormModal
            persons={persons}
            salespeople={salespeople}
            branches={branches}
            initialQuery={roleFormRequest.initialQuery}
            initialBranchId={needSalesBranchFilter ? (salesBranchFilter || undefined) : null}
            onSubmit={(fields) => {
              const id = onCreateSalespersonRole(fields);
              if (id) {
                if (needSalesBranchFilter) {
                  // Cabang-filtered context: only select/follow the new role if it's actually
                  // usable here (a real branch, matching this transaction's own anchor) — a
                  // role deliberately created as Pusat here is saved either way but isn't valid
                  // for THIS Cabang-anchored transaction, so it's left unselected rather than
                  // shown as picked while the picker's own filter would immediately hide it.
                  if (fields.branchId) {
                    setSalesBranchFilter(fields.branchId);
                    commitSalespersonSelection(id);
                  }
                } else if (fields.branchId === null) {
                  // Pusat-anchored sale: select the new role only when it IS a Pusat role. A role
                  // deliberately created at a branch here is saved but can't be used on this sale
                  // (the write path refuses a mismatched anchor), so it's left unselected — never
                  // shown as picked while the Pusat-filtered picker hides it.
                  commitSalespersonSelection(id);
                }
              }
              return id;
            }}
            onClose={() => setRoleFormRequest(null)}
          />
        )}

        {isPembelian && (
          <div className="form-row">
            <label className="form-label" htmlFor="txn-form-no-invoice-penjual">No. Invoice Penjual</label>
            <input id="txn-form-no-invoice-penjual" className="form-input" value={supplierInvoiceNo}
              onChange={(e) => setSupplierInvoiceNo(e.target.value)} />
            {!supplierInvoiceNo.trim() && <div className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</div>}
          </div>
        )}

        <div className="card">
          <div className="card__title">Barang</div>
          {lines.length === 0 && <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>Belum ada baris.</div>}
          {lines.length === 0 && (
            <div className="form-hint" style={{ color: "var(--red)" }}>Tambahkan minimal satu barang sebelum menyimpan.</div>
          )}
          {lines.map((l, i) => {
            // Bug fix: this used to destructure `before` from `lineStock(l)` too (i.e.
            // `preview.before`) — correct in create mode (identical to `liveStock` there, since
            // there's no old effect to reverse) but WRONG in edit mode, where `preview.before`
            // is a synthetic baseline (current stock with THIS transaction's own old effect
            // already reversed out) that never existed on the books. "Stok saat ini" means
            // "current stock" — `liveStock(l)` is the only value that's actually true under that
            // label; `stockAfter` stays sourced from `lineStock(l)` unchanged, since
            // `preview.after` is the real, correctly-computed resulting stock either way.
            const stockBefore = liveStock(l);
            const { after: stockAfter } = lineStock(l);
            const wac = avgCosts ? avgCosts.get(l.internalCode) : null;
            const qty = Number(l.qty) || 0;
            const subtotal = qty * (Number(l.unitPrice) || 0);
            const goesNegative = !!l.internalCode && stockAfter < 0;
            const isDuplicate = !!l.internalCode && (duplicateCodeCounts.get(l.internalCode) || 0) > 1;
            // Manual-testing report: in edit mode, a line whose qty/item hasn't actually been
            // touched shows "Stok saat ini: 475 → 475" — both numbers are genuinely true
            // (liveStock(l) and preview.after are exactly what they claim to be, untouched by
            // this fix), but the SAME number on both sides of an arrow reads as broken, not as
            // "nothing changed." Create mode never hits this (stockBefore/stockAfter already
            // differ there whenever a real qty is entered — if they happen to coincide it's
            // because nothing meaningful was entered yet, not because of a no-op edit), so this
            // is gated on `isEdit` only.
            const showStockArrow = !isEdit || stockBefore !== stockAfter;
            const substitutes = substituteSuggestions(l);
            return (
              <div className="txn-item-card" key={i}>
                <div className="txn-item-card__pick-row" style={{ display: "flex", gap: "var(--gap-sm)", alignItems: "center" }}>
                  <label className="form-label" style={{ marginBottom: 0, flex: "0 0 auto" }}>Nama Barang</label>
                  <button type="button" className="line-pick" style={{ flex: 1 }} onClick={() => openReplacePicker(i)}>
                    <span>
                      {l.internalCode
                        ? (<><span className="mono">{l.internalCode}</span> · {l.name}</>)
                        : <span className="form-hint">— pilih barang —</span>}
                    </span>
                    <span className="line-pick__change">ganti</span>
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" title="Hapus baris" onClick={() => removeLine(i)}>✕</button>
                </div>

                <div className="txn-item-card__fields">
                  <div className="txn-item-card__field">
                    <label className="form-label" htmlFor={`txn-line-qty-${i}`}>Quantity</label>
                    <input id={`txn-line-qty-${i}`} className="form-input num-input" type="number" min="1" value={l.qty}
                      onChange={(e) => setLine(i, { qty: e.target.value })} />
                  </div>
                  <div className="txn-item-card__field">
                    <label className="form-label" htmlFor={`txn-line-harga-${i}`}>Harga Per Item</label>
                    <div className="currency-input">
                      <span className="currency-input__prefix">Rp</span>
                      <input id={`txn-line-harga-${i}`} type="text" inputMode="numeric" value={formatCurrencyInput(l.unitPrice)}
                        onChange={(e) => setLine(i, { unitPrice: parseCurrencyInput(e.target.value) })} />
                    </div>
                  </div>
                  <div className="txn-item-card__field">
                    <label className="form-label">Subtotal</label>
                    <div className="txn-item-card__subtotal">Rp {fmtQty(subtotal)}</div>
                  </div>
                </div>

                <div className="txn-item-card__helpers">
                  <span className={"txn-item-card__stock" + (goesNegative ? " txn-item-card__stock--neg" : "")}>
                    {showStockArrow
                      ? <>Stok saat ini: {fmtQty(stockBefore)} → {fmtQty(stockAfter)}{goesNegative ? " ⚠" : ""}</>
                      : <>Stok saat ini: {fmtQty(stockBefore)}{goesNegative ? " ⚠" : ""}</>}
                  </span>
                  {txnType === "PENJUALAN" && (
                    <span className="txn-item-card__wac">
                      Modal rata²: {wac != null ? `Rp ${formatWac(wac)}` : "—"}
                    </span>
                  )}
                  {isDuplicate && (
                    <span className="txn-item-card__stock--neg" style={{ flexBasis: "100%" }}>
                      ⚠ Barang ini sudah ada di baris lain.
                    </span>
                  )}
                  {l.substituteFor && (
                    <span className="chip chip--muted txn-substitute-chip">Pengganti untuk {l.substituteFor}</span>
                  )}
                </div>
                {substitutes.length > 0 && (
                  <div className="txn-substitutes">
                    <div className="txn-substitutes__title">Saudara yang ada stok di {location === "CABANG" ? "Cabang" : "Pusat"}:</div>
                    {substitutes.map((s) => (
                      <div className="txn-substitutes__row" key={s.code}>
                        <span><span className="mono">{s.code}</span> · {s.name} <span className="txn-substitutes__stock">(stok {fmtQty(s.stock)})</span></span>
                        <button type="button" className="btn btn-sm" onClick={() => applySubstitute(i, s)}>Pakai</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <button type="button" className="btn btn-sm" onClick={openAddPicker}>+ Pilih barang</button>
        </div>

        {costItems.length > 0 && (
          <div className="card">
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Subtotal Barang</span><b>Rp {fmtQty(goodsTotal)}</b>
            </div>
          </div>
        )}

        <CostItemsEditor items={costItems} onChange={setCostItems} txnType={txnType} checkResult={costItemsCheck} />

        <div className="alert alert--info" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Total Nilai</span><b>Rp {fmtQty(total)}</b>
        </div>
        {!totalCheck.ok && <div className="form-hint" style={{ color: "var(--red)" }}>{totalCheck.error}</div>}

        <BiayaLainEditor rows={biayaLain} onChange={setBiayaLain} txnType={txnType} />

        {bothBucketsLive ? (
          <>
            <div className="form-row">
              <label className="form-label">Status Pembayaran Barang</label>
              <div className="seg">
                <button type="button" className={barangLunas ? "active" : ""} onClick={() => setBarangLunas(true)}>Lunas</button>
                <button type="button" className={!barangLunas ? "active" : ""} onClick={() => setBarangLunas(false)}>Belum Lunas</button>
              </div>
            </div>
            <div className="form-row">
              <label className="form-label">Status Pembayaran Tagihan & Potongan</label>
              <div className="seg">
                <button type="button" className={tagihanPotonganLunas ? "active" : ""} onClick={() => setTagihanPotonganLunas(true)}>Lunas</button>
                <button type="button" className={!tagihanPotonganLunas ? "active" : ""} onClick={() => setTagihanPotonganLunas(false)}>Belum Lunas</button>
              </div>
            </div>
            <div className="form-row">
              <label className="form-label">Status Keseluruhan</label>
              <StatusPill status={effectiveIsLunas ? "LUNAS" : (isPembelian ? "UTANG" : "PIUTANG")} />
            </div>
          </>
        ) : (
          <div className="form-row">
            <label className="form-label">Status Pembayaran</label>
            <div className="seg">
              <button type="button" className={isLunas ? "active" : ""} onClick={() => toggleLunas(true)}>Lunas</button>
              <button type="button" className={!isLunas ? "active" : ""} onClick={() => toggleLunas(false)}>Belum Lunas</button>
            </div>
          </div>
        )}
        {!effectiveIsLunas && (
          <div className="form-row">
            <label className="form-label" htmlFor="txn-form-tempo-pembayaran">Tempo Pembayaran (Hari)</label>
            <input id="txn-form-tempo-pembayaran" className="form-input" type="number" min="1" value={dueDaysText}
              onChange={(e) => setDueDaysText(e.target.value)}
              onBlur={() => { if (dueDaysNum != null) setDueDaysText(String(dueDaysNum)); }} />
            {dueDate
              ? <div className="form-hint">Jatuh tempo: {fmtDateShort(dueDate)}</div>
              : <div className="form-hint" style={{ color: "var(--red)" }}>Tempo Pembayaran wajib diisi, minimal 1 hari.</div>}
          </div>
        )}
        <div className="card">
          <div className="card__title">Pembayaran</div>
          {payments.length === 0 && <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>Belum ada baris pembayaran.</div>}
          {payments.map((p, i) => (
            <div className="payment-row payment-row--editable" key={p.id ?? i} style={{ marginBottom: "var(--gap-sm)" }}>
              <div style={{ display: "flex", gap: "var(--gap-md)", alignItems: "flex-end" }}>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`txn-pay-metode-${i}`}>Metode</label>
                  <select id={`txn-pay-metode-${i}`} className="form-select" value={p.method} onChange={(e) => setPaymentMethod(i, e.target.value)}>
                    {!p.method && <option value="" disabled>— pilih metode —</option>}
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
                  </select>
                </div>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`txn-pay-jumlah-${i}`}>Jumlah</label>
                  <div className="currency-input">
                    <span className="currency-input__prefix">Rp</span>
                    <input id={`txn-pay-jumlah-${i}`} type="text" inputMode="numeric" value={formatCurrencyInput(p.amount)}
                      onChange={(e) => setPaymentAmount(i, parseCurrencyInput(e.target.value))} />
                  </div>
                </div>
                <button type="button" className="btn btn-ghost btn-sm" title="Hapus baris" onClick={() => removePayment(i)}>✕</button>
              </div>
              {showBucketUI && (() => {
                const s = rowSplit(p);
                return (
                  <div style={{ marginTop: "var(--gap-sm)" }}>
                    <div className="form-row" style={{ marginBottom: "var(--gap-xs)" }}>
                      <label className="form-label" style={{ marginBottom: 0 }}>Rincian Pembagian</label>
                    </div>
                    <div style={{ display: "flex", gap: "var(--gap-md)", alignItems: "flex-end" }}>
                      <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                        <label className="form-label" htmlFor={`txn-pay-split-barang-${i}`}>Untuk Barang</label>
                        <div className="currency-input">
                          <span className="currency-input__prefix">Rp</span>
                          <input id={`txn-pay-split-barang-${i}`} type="text" inputMode="numeric" value={formatCurrencyInput(s.barang)}
                            onChange={(e) => setPaymentSplitBarang(i, parseCurrencyInput(e.target.value))} />
                        </div>
                      </div>
                      <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                        <label className="form-label" htmlFor={`txn-pay-split-tagihan-${i}`}>Untuk Tagihan & Potongan</label>
                        <div className="currency-input">
                          <span className="currency-input__prefix">Rp</span>
                          <input id={`txn-pay-split-tagihan-${i}`} type="text" inputMode="numeric" value={formatCurrencyInput(s.tagihanPotongan)}
                            onChange={(e) => setPaymentSplitTagihanPotongan(i, parseCurrencyInput(e.target.value))} />
                        </div>
                      </div>
                    </div>
                    {validateSplitSum(p.amount, s.barang, s.tagihanPotongan)
                      ? <div className="form-hint" style={{ color: "var(--green)" }}>✓ Sesuai</div>
                      : <div className="form-hint" style={{ color: "var(--red)" }}>Rincian pembagian harus berjumlah sama dengan Jumlah.</div>}
                  </div>
                );
              })()}
              <div style={{ display: "flex", gap: "var(--gap-md)", alignItems: "flex-end", marginTop: "var(--gap-sm)" }}>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`txn-pay-tgl-${i}`}>Tanggal Bayar</label>
                  <input id={`txn-pay-tgl-${i}`} className="form-input" type="date" value={p.paidAtDate}
                    onChange={(e) => setPaymentPaidAtDate(i, e.target.value)} />
                </div>
                <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`txn-pay-waktu-${i}`}>Waktu Bayar</label>
                  <input id={`txn-pay-waktu-${i}`} className="form-input" type="time" value={p.paidAtTime}
                    onChange={(e) => setPaymentPaidAtTime(i, e.target.value)} />
                </div>
              </div>
              {duplicatePaymentIndexes.has(i) && (
                <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-xs)" }}>
                  Sudah ada pembayaran dengan metode, tanggal, dan waktu yang sama.
                </div>
              )}
              {!(Number(p.amount) > 0) && (
                <div className="form-hint" style={{ color: "var(--red)", marginTop: "var(--gap-xs)" }}>
                  Jumlah harus lebih dari Rp 0.
                </div>
              )}
              {p.method === "LAINNYA" && (
                <div className="form-row" style={{ marginTop: "var(--gap-sm)", marginBottom: 0 }}>
                  <label className="form-label" htmlFor={`txn-pay-keterangan-${i}`}>Keterangan</label>
                  <input id={`txn-pay-keterangan-${i}`} className="form-input" value={p.methodNote || ""} onChange={(e) => setPaymentNote(i, e.target.value)} />
                </div>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={addPayment}>+ Tambah metode</button>
          <div className="alert alert--info" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "var(--gap-md)" }}>
            <span>Total dibayar</span><b>Rp {fmtQty(payments.reduce((s, p) => s + (Number(p.amount) || 0), 0))}</b>
          </div>
          {/* Issue 2 (2nd-round fix): both bucket messages moved here, into ONE shared location,
              from their old spot directly under each bucket's own toggle — each message already
              names its own bucket ("Barang..."/"Status Pembayaran Tagihan & Potongan..."), so no
              identification is lost. Gated on `payments.length > 0` — a DISPLAY-ONLY gate; the
              underlying `barangMessage`/`tagihanMessage` values (and therefore `canSubmit`) are
              computed exactly as before, completely independent of this gate — a transaction with
              zero payment rows and a bucket toggle set to Lunas still correctly disables Simpan,
              the message just isn't shown until a row exists to attach it to. Both can genuinely
              fire at once (Barang and Tagihan & Potongan are checked independently) — simply
              stacked, Barang first, matching this form's own existing convention for multiple
              simultaneous inline validation messages in this same card (no separator/lead-in). */}
          {payments.length > 0 && barangMessage && (
            <div className="form-hint" style={{ color: "var(--red)" }}>{barangMessage}</div>
          )}
          {payments.length > 0 && tagihanMessage && (
            <div className="form-hint" style={{ color: "var(--red)" }}>{tagihanMessage}</div>
          )}
          {/* Single-toggle redesign: the toggle-agnostic state-2 equivalent of the two messages
              above — rendered only when `!bothBucketsLive` (there, `barangMessage`/`tagihanMessage`
              are never computed at all, so there's no risk of the same violation showing twice).
              Same stacking convention, Barang before Tagihan & Potongan. */}
          {payments.length > 0 && showBucketUI && !bothBucketsLive && barangOverAllocMessage && (
            <div className="form-hint" style={{ color: "var(--red)" }}>{barangOverAllocMessage}</div>
          )}
          {payments.length > 0 && showBucketUI && !bothBucketsLive && tagihanOverAllocMessage && (
            <div className="form-hint" style={{ color: "var(--red)" }}>{tagihanOverAllocMessage}</div>
          )}
          {/* Gated on totalCheck.ok: once Total Nilai itself is negative, paymentsCheck's own
              sum<=total invariant is mathematically guaranteed to also fail (any real payment
              sum is >= 0, so it always "exceeds" a negative total) — showing that message here
              too would blame payments for a problem that's actually the discount/cost items,
              the exact confusion this whole feature exists to stop. Once totalCheck.ok is true
              again, paymentsCheck's own message (a genuine payment-shaped problem) shows as
              before — this gate only ever suppresses the negative-total false alarm. */}
          {totalCheck.ok && !paymentsCheck.ok && <div className="form-hint" style={{ color: "var(--red)" }}>{paymentsCheck.error}</div>}
          {hasDuplicateNewMethod && (
            <div className="form-hint" style={{ color: "var(--red)" }}>
              Dua baris pembayaran baru tidak boleh memakai metode yang sama.
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
          <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
            {isEdit ? "Simpan" : "Simpan Transaksi"}
          </button>
        </div>
      </form>

      {pickerMode && (
        <ItemPickerModal items={items} bucket={location} multi={pickerMode === "add"}
          onConfirm={confirmPicker} onClose={closePicker} />
      )}

      {/* `pendingDraft` is shared between this modal and SalesRemintWarningModal below — only one
          is ever actually pending at a time, but `submit()` sets `pendingDraft` for BOTH before
          `warningLines` is ever populated, so this must also check `warningLines.length > 0`
          (only ever set by `proceedToStockCheck`) rather than `pendingDraft` alone — otherwise
          this would render (with a stale, empty `warningLines`) at the same time as the remint
          warning below, before the stock check has even run. */}
      {pendingDraft && warningLines.length > 0 && (
        <StockWarningModal lines={warningLines} txnType={txnType} onProceed={proceedAnyway} onCancel={cancelAndCorrect} />
      )}

      {pendingRemintWarning && (
        <SalesRemintWarningModal
          oldName={pendingRemintWarning.oldName}
          newName={pendingRemintWarning.newName}
          oldInvoiceNumber={pendingRemintWarning.oldInvoiceNumber}
          onProceed={proceedPastRemintWarning}
          onCancel={cancelRemintWarning}
        />
      )}
    </div>
  );
}
